/* eslint-disable max-params, max-lines -- mock data file; refactor to options objects + split in future */
import type { SourceEvidence, SourceRef, SourceSnippet, TenderCase, TenderRun } from "../types"

function snippet(
  documentId: string,
  documentName: string,
  page: number,
  quote: string,
  chunkText?: string
): SourceSnippet {
  return { kind: "file", documentId, documentName, page, quote, chunkText }
}

function librarySnippet(
  libraryId: string,
  libraryName: string,
  documentName: string,
  quote: string,
  chunkText?: string,
  page?: number
): SourceSnippet {
  return { kind: "library", libraryId, libraryName, documentName, page, quote, chunkText }
}

function evidence(
  reasoning: string,
  snippets: SourceSnippet[],
  confidence: SourceEvidence["confidence"],
  verificationStatus?: SourceEvidence["verification"]
): SourceEvidence {
  return { reasoning, snippets, confidence, verification: verificationStatus }
}

// ---------------------------------------------------------------------------
// Document name constants for case-001
// ---------------------------------------------------------------------------

const FRF = "Förfrågan_Ramavtal_IT-konsult_RS_2026.pdf"
const BIL1 = "Bilaga_1_Kravspecifikation.pdf"
const BIL2 = "Bilaga_2_Avtalsvillkor_och_SLA.pdf"
const BIL3 = "Bilaga_3_Prisbilaga_och_Volymer.pdf"
const CV_POOL = "Konsult-CV_Pool_IT-arkitekter_2026.pdf"

// ---------------------------------------------------------------------------
// case-001: Run 1 — initial 2 docs, extraction only
// ---------------------------------------------------------------------------

const CASE_001_RUN_1: TenderRun = {
  id: "run-001-1",
  runNumber: 1,
  createdAt: "2025-11-18T09:30:00Z",
  procurementDocuments: [
    {
      id: "doc-001",
      filename: FRF,
      uploadedAt: "2025-11-18T09:31:00Z",
      status: "indexerad",
      pages: 48,
      sizeKb: 2340,
    },
    {
      id: "doc-002",
      filename: BIL1,
      uploadedAt: "2025-11-18T09:32:00Z",
      status: "indexerad",
      pages: 22,
      sizeKb: 1180,
    },
  ],
  contextDocuments: [],
  bidDocuments: [],
  cvDocuments: [],
  requirements: [
    {
      id: "SKA-001",
      description: "Leverantören ska inneha F-skattsedel och vara registrerad för moms.",
      source: { kind: "file", documentName: FRF, page: 7 } satisfies SourceRef,
      confidence: "saker",
      category: "Formella krav",
      isMandatory: true,
    },
    {
      id: "SKA-002",
      description: "Leverantören ska ha minst 5 anställda konsulter med relevant IT-kompetens.",
      source: { kind: "file", documentName: FRF, page: 8 } satisfies SourceRef,
      confidence: "saker",
      category: "Kapacitetskrav",
      isMandatory: true,
    },
    {
      id: "SKA-003",
      description:
        "Minst en (1) konsult ska ha dokumenterad erfarenhet av agil projektledning (Scrum/SAFe).",
      source: { kind: "file", documentName: BIL1, page: 4 } satisfies SourceRef,
      confidence: "saker",
      category: "Kompetensskrav",
      isMandatory: true,
    },
    {
      id: "SKA-004",
      description: "Leverantören ska kunna påbörja uppdrag inom 10 arbetsdagar från avropad order.",
      source: { kind: "file", documentName: FRF, page: 12 } satisfies SourceRef,
      confidence: "saker",
      category: "Tillgänglighet",
      isMandatory: true,
    },
    {
      id: "SKA-005",
      description:
        "Leverantören ska ha certifierad informationssäkerhetspolicy enligt ISO 27001 eller motsvarande.",
      source: { kind: "file", documentName: BIL1, page: 6 } satisfies SourceRef,
      confidence: "osaker",
      category: "Säkerhetskrav",
      isMandatory: true,
    },
  ],
  evaluationCriteria: [
    {
      id: "UTV-001",
      name: "Pris",
      weightPercent: 40,
      description: "Timpriser för respektive konsultprofil viktade mot avropade volymer.",
      source: { kind: "file", documentName: FRF, page: 18 } satisfies SourceRef,
      confidence: "saker",
    },
    {
      id: "UTV-002",
      name: "Kompetens och erfarenhet",
      weightPercent: 30,
      description: "Bedömning av konsultprofilernas CV, certifieringar och referensuppdrag.",
      source: { kind: "file", documentName: FRF, page: 19 } satisfies SourceRef,
      confidence: "saker",
    },
  ],
  contractTerms: [],
  criticalDates: [
    {
      id: "DATUM-001",
      event: "Sista dag för frågor och förtydliganden",
      date: "2025-12-05",
      daysRemaining: 15,
      urgency: "varning",
    },
    {
      id: "DATUM-006",
      event: "Obligatorisk leverantörspresentation",
      date: "2025-11-28",
      daysRemaining: 8,
      urgency: "kritisk",
    },
  ],
  requirementMatrix: [],
  risks: [],
  scorecardDimensions: [],
  recommendation: null,
  roleRequirements: [],
  bidDeliverables: [],
}

// ---------------------------------------------------------------------------
// case-001: Run 2 — 4 docs + context, complete analysis
// ---------------------------------------------------------------------------

const CASE_001_RUN_2: TenderRun = {
  id: "run-001-2",
  runNumber: 2,
  createdAt: "2025-11-20T09:00:00Z",
  procurementDocuments: [
    {
      id: "doc-001",
      filename: FRF,
      uploadedAt: "2025-11-18T09:31:00Z",
      status: "indexerad",
      pages: 48,
      sizeKb: 2340,
    },
    {
      id: "doc-002",
      filename: BIL1,
      uploadedAt: "2025-11-18T09:32:00Z",
      status: "indexerad",
      pages: 22,
      sizeKb: 1180,
    },
    {
      id: "doc-003",
      filename: BIL2,
      uploadedAt: "2025-11-18T09:33:00Z",
      status: "indexerad",
      pages: 31,
      sizeKb: 1560,
    },
    {
      id: "doc-004",
      filename: BIL3,
      uploadedAt: "2025-11-18T09:34:00Z",
      status: "indexerad",
      pages: 8,
      sizeKb: 420,
    },
  ],
  contextDocuments: [
    {
      id: "doc-005",
      filename: CV_POOL,
      uploadedAt: "2025-11-18T09:37:00Z",
      status: "indexerad",
      pages: 12,
      sizeKb: 610,
    },
  ],
  bidDocuments: [],
  cvDocuments: [],

  // -----------------------------------------------------------------------
  // Requirements (step 2) — existing sourceDoc/sourcePage kept, snippet added
  // -----------------------------------------------------------------------
  requirements: [
    {
      id: "SKA-001",
      description: "Leverantören ska inneha F-skattsedel och vara registrerad för moms.",
      source: { kind: "file", documentName: FRF, page: 7 } satisfies SourceRef,
      confidence: "saker",
      category: "Formella krav",
      isMandatory: true,
      snippet: snippet(
        "doc-001",
        FRF,
        7,
        "Leverantören ska inneha F-skattsedel och vara registrerad för mervärdesskatt (moms) vid tidpunkten för anbudslämning.",
        "3.1 Obligatoriska krav (ska-krav)\nLeverantören ska inneha F-skattsedel och vara registrerad för mervärdesskatt (moms) vid tidpunkten för anbudslämning. Uppfyllnad ska styrkas med utdrag från Skatteverket daterat ej tidigare än 3 månader före sista anbudsdag."
      ),
    },
    {
      id: "SKA-002",
      description: "Leverantören ska ha minst 5 anställda konsulter med relevant IT-kompetens.",
      source: { kind: "file", documentName: FRF, page: 8 } satisfies SourceRef,
      confidence: "saker",
      category: "Kapacitetskrav",
      isMandatory: true,
      snippet: snippet(
        "doc-001",
        FRF,
        8,
        "Leverantören ska vid anbudstillfället ha minst fem (5) fast anställda konsulter med dokumenterad IT-kompetens.",
        "3.2 Kapacitetskrav\nLeverantören ska vid anbudstillfället ha minst fem (5) fast anställda konsulter med dokumenterad IT-kompetens. Med IT-kompetens avses minst 2 års yrkeserfarenhet inom systemutveckling, arkitektur, projektledning eller IT-infrastruktur."
      ),
    },
    {
      id: "SKA-003",
      description:
        "Minst en (1) konsult ska ha dokumenterad erfarenhet av agil projektledning (Scrum/SAFe).",
      source: { kind: "file", documentName: BIL1, page: 4 } satisfies SourceRef,
      confidence: "saker",
      category: "Kompetensskrav",
      isMandatory: true,
      snippet: snippet(
        "doc-002",
        BIL1,
        4,
        "Minst en (1) konsult i leverantörens organisation ska ha dokumenterad och verifierbar erfarenhet av agil projektledning, specifikt Scrum eller SAFe.",
        "2.1 Specifika kompetensskrav\nMinst en (1) konsult i leverantörens organisation ska ha dokumenterad och verifierbar erfarenhet av agil projektledning, specifikt Scrum eller SAFe. Certifiering (t.ex. Certified Scrum Master, SAFe Agilist) är meriterande men inte obligatoriskt."
      ),
    },
    {
      id: "SKA-004",
      description: "Leverantören ska kunna påbörja uppdrag inom 10 arbetsdagar från avropad order.",
      source: { kind: "file", documentName: FRF, page: 12 } satisfies SourceRef,
      confidence: "saker",
      category: "Tillgänglighet",
      isMandatory: true,
      snippet: snippet(
        "doc-001",
        FRF,
        12,
        "Leverantören ska kunna påbörja leverans av beställda konsulttjänster inom tio (10) arbetsdagar från datum för avropsbekräftelse.",
        "4.3 Tillgänglighet och svarstider\nLeverantören ska kunna påbörja leverans av beställda konsulttjänster inom tio (10) arbetsdagar från datum för avropsbekräftelse. Vid akuta behov ska leverantören sträva efter att leverera inom fem (5) arbetsdagar."
      ),
    },
    {
      id: "SKA-005",
      description:
        "Leverantören ska ha certifierad informationssäkerhetspolicy enligt ISO 27001 eller motsvarande.",
      source: { kind: "file", documentName: BIL1, page: 6 } satisfies SourceRef,
      confidence: "osaker",
      category: "Säkerhetskrav",
      isMandatory: true,
      snippet: snippet(
        "doc-002",
        BIL1,
        6,
        "Leverantören ska ha en certifierad informationssäkerhetspolicy enligt ISO/IEC 27001 eller motsvarande erkänd standard.",
        "2.3 Informationssäkerhet\nLeverantören ska ha en certifierad informationssäkerhetspolicy enligt ISO/IEC 27001 eller motsvarande erkänd standard. Giltigt certifikat eller likvärdigt intyg ska bifogas anbudet. Certifikatet ska vara utfärdat av ett ackrediterat certifieringsorgan."
      ),
    },
    {
      id: "SKA-006",
      description: "Konsulter ska kunna kommunicera obehindrat på svenska i tal och skrift.",
      source: { kind: "file", documentName: FRF, page: 9 } satisfies SourceRef,
      confidence: "saker",
      category: "Kompetensskrav",
      isMandatory: true,
      snippet: snippet(
        "doc-001",
        FRF,
        9,
        "Samtliga konsulter som arbetar inom ramen för ramavtalet ska behärska svenska i tal och skrift på minst nivå C1 enligt CEFR.",
        "3.3 Språkkrav\nSamtliga konsulter som arbetar inom ramen för ramavtalet ska behärska svenska i tal och skrift på minst nivå C1 enligt CEFR. Bevis på språkkunskaper kan krävas vid tveksamhet."
      ),
    },
    {
      id: "SKA-007",
      description:
        "Leverantören ska acceptera Region Stockholms allmänna avtalsvillkor utan modifikationer.",
      source: { kind: "file", documentName: BIL2, page: 2 } satisfies SourceRef,
      confidence: "osaker",
      category: "Avtalsvillkor",
      isMandatory: true,
      snippet: snippet(
        "doc-003",
        BIL2,
        2,
        "Leverantören ska utan reservation acceptera och underteckna de allmänna avtalsvillkor som framgår av denna bilaga.",
        "1.1 Avtalets omfattning\nLeverantören ska utan reservation acceptera och underteckna de allmänna avtalsvillkor som framgår av denna bilaga. Inga avvikelser, tillägg eller förtydliganden accepteras utan skriftligt medgivande från beställaren."
      ),
    },
    {
      id: "SKA-008",
      description:
        "Leverantören ska redovisa referensuppdrag från minst 2 offentliga kunder under de senaste 3 åren.",
      source: { kind: "file", documentName: FRF, page: 11 } satisfies SourceRef,
      confidence: "saker",
      category: "Erfarenhetskrav",
      isMandatory: true,
      snippet: snippet(
        "doc-001",
        FRF,
        11,
        "Leverantören ska redovisa minst två (2) genomförda referensuppdrag hos offentliga beställare utförda under de senaste tre (3) åren.",
        "3.5 Erfarenhetskrav\nLeverantören ska redovisa minst två (2) genomförda referensuppdrag hos offentliga beställare utförda under de senaste tre (3) åren. Uppdragen ska ha haft ett kontraktsvärde om minst 500 000 SEK vardera."
      ),
    },
    {
      id: "SKA-009",
      description: "Leverantören ska kunna erbjuda konsulter med säkerhetsgodkännande nivå C.",
      source: { kind: "file", documentName: BIL1, page: 8 } satisfies SourceRef,
      confidence: "osaker",
      category: "Säkerhetskrav",
      isMandatory: true,
      snippet: snippet(
        "doc-002",
        BIL1,
        8,
        "För uppdrag som involverar känslig information ska leverantören kunna tillhandahålla konsulter med säkerhetsgodkännande av lägst nivå C i enlighet med säkerhetsskyddslagen.",
        "2.4 Säkerhetsgodkännande\nFör uppdrag som involverar känslig information ska leverantören kunna tillhandahålla konsulter med säkerhetsgodkännande av lägst nivå C i enlighet med säkerhetsskyddslagen (2018:585). Godkännandet ska vara aktivt och verifierbart vid avropstillfället."
      ),
    },
    {
      id: "SKA-010",
      description:
        "Leverantören ska tillhandahålla månatliga rapporter om uppdragsstatus och avvikelser.",
      source: { kind: "file", documentName: BIL2, page: 14 } satisfies SourceRef,
      confidence: "saker",
      category: "Rapporteringskrav",
      isMandatory: true,
      snippet: snippet(
        "doc-003",
        BIL2,
        14,
        "Leverantören ska månadsvis, senast den 5:e i påföljande månad, lämna en skriftlig statusrapport för respektive pågående uppdrag.",
        "5.2 Rapportering\nLeverantören ska månadsvis, senast den 5:e i påföljande månad, lämna en skriftlig statusrapport för respektive pågående uppdrag. Rapporten ska innehålla status, avvikelser, riskbild och förbrukning mot budget."
      ),
    },
    // Meriterande krav
    {
      id: "MER-001",
      description:
        "Certifiering inom molntjänster (t.ex. AWS Solutions Architect, Azure Administrator) är meriterande.",
      source: { kind: "file", documentName: BIL1, page: 13 } satisfies SourceRef,
      confidence: "saker",
      category: "Kompetensskrav",
      isMandatory: false,
      snippet: snippet(
        "doc-002",
        BIL1,
        13,
        "Certifiering inom molntjänster från ledande leverantör (AWS, Azure, GCP) bedöms som meriterande vid utvärdering av kompetens och erfarenhet.",
        "4.2 Meriterande kompetens\nCertifiering inom molntjänster från ledande leverantör (AWS, Azure, GCP) bedöms som meriterande vid utvärdering av kompetens och erfarenhet."
      ),
    },
    {
      id: "MER-002",
      description:
        "Erfarenhet av systemintegration mot vårdinformationssystem (VIS/PAS) är meriterande.",
      source: { kind: "file", documentName: BIL1, page: 14 } satisfies SourceRef,
      confidence: "osaker",
      category: "Erfarenhetskrav",
      isMandatory: false,
      snippet: snippet(
        "doc-002",
        BIL1,
        14,
        "Erfarenhet av systemintegration mot vårdinformationssystem eller liknande komplexa ärendesystem bedöms som meriterande.",
        "4.3 Meriterande erfarenhet\nErfarenhet av systemintegration mot vårdinformationssystem eller liknande komplexa ärendesystem bedöms som meriterande."
      ),
    },
  ],

  // -----------------------------------------------------------------------
  // Evaluation criteria
  // -----------------------------------------------------------------------
  evaluationCriteria: [
    {
      id: "UTV-001",
      name: "Pris",
      weightPercent: 40,
      description: "Timpriser för respektive konsultprofil viktade mot avropade volymer.",
      source: { kind: "file", documentName: FRF, page: 18 } satisfies SourceRef,
      confidence: "saker",
      snippet: snippet(
        "doc-001",
        FRF,
        18,
        "Priskriteriet viktas med 40 % av det totala anbudsvärdet och utvärderas baserat på angivna timpriser per konsultprofil multiplicerat med uppskattade avropade volymer.",
        "6.1 Utvärderingskriterier\nPriskriteriet viktas med 40 % av det totala anbudsvärdet och utvärderas baserat på angivna timpriser per konsultprofil multiplicerat med uppskattade avropade volymer enligt Bilaga 3."
      ),
    },
    {
      id: "UTV-002",
      name: "Kompetens och erfarenhet",
      weightPercent: 30,
      description: "Bedömning av konsultprofilernas CV, certifieringar och referensuppdrag.",
      source: { kind: "file", documentName: FRF, page: 19 } satisfies SourceRef,
      confidence: "saker",
      snippet: snippet(
        "doc-001",
        FRF,
        19,
        "Kompetens och erfarenhet utvärderas med 30 % vikt baserat på inlämnade CV, certifieringsintyg och redovisade referensuppdrag.",
        "6.2 Kompetens och erfarenhet (30 %)\nKompetens och erfarenhet utvärderas med 30 % vikt baserat på inlämnade CV, certifieringsintyg och redovisade referensuppdrag. Varje konsultprofil poängsätts individuellt och viktas mot den procentuella andelen av estimerade avrop."
      ),
    },
    {
      id: "UTV-003",
      name: "Metodkompetens",
      weightPercent: 15,
      description: "Erfarenhet av agila metoder, DevOps och moderna systemutvecklingsprocesser.",
      source: { kind: "file", documentName: BIL1, page: 12 } satisfies SourceRef,
      confidence: "saker",
      snippet: snippet(
        "doc-002",
        BIL1,
        12,
        "Metodkompetens bedöms med 15 % vikt och omfattar dokumenterad erfarenhet av agila metoder, DevOps-praktiker och moderna CI/CD-processer.",
        "4.1 Metodkompetens (15 %)\nMetodkompetens bedöms med 15 % vikt och omfattar dokumenterad erfarenhet av agila metoder, DevOps-praktiker och moderna CI/CD-processer. Svar ska ges i form av konkreta exempel från genomförda uppdrag."
      ),
    },
    {
      id: "UTV-004",
      name: "Tillgänglighet och flexibilitet",
      weightPercent: 10,
      description: "Förmåga att snabbt ställa om konsultpool och täcka breda kompetensområden.",
      source: { kind: "file", documentName: FRF, page: 20 } satisfies SourceRef,
      confidence: "saker",
      snippet: snippet(
        "doc-001",
        FRF,
        20,
        "Tillgänglighet och flexibilitet viktas med 10 % och utvärderas på leverantörens förmåga att leverera konsulter inom begärda tidramar och täcka en bredd av kompetensområden.",
        "6.4 Tillgänglighet och flexibilitet (10 %)\nTillgänglighet och flexibilitet viktas med 10 % och utvärderas på leverantörens förmåga att leverera konsulter inom begärda tidramar och täcka en bredd av kompetensområden vid behov."
      ),
    },
    {
      id: "UTV-005",
      name: "Hållbarhet och sociala krav",
      weightPercent: 5,
      description: "Leverantörens hållbarhetsarbete och uppfyllnad av sociala krav.",
      source: { kind: "file", documentName: FRF, page: 21 } satisfies SourceRef,
      confidence: "osaker",
      // Library snippet: criterion cross-referenced against ILO core conventions
      snippet: librarySnippet(
        "standards",
        "Standardbibliotek",
        "ILO Kärnkonventioner — sammanfattning",
        "ILO:s åtta kärnkonventioner fastställer grundläggande rättigheter i arbetslivet, inklusive förbud mot tvångsarbete, barnarbete och diskriminering, samt rätten till kollektiva förhandlingar.",
        "ILO-konventionerna 87, 98, 29, 105, 138, 182, 100, 111 utgör grunden för sociala minimikrav i offentlig upphandling enligt LOU kap. 17."
      ),
    },
  ],

  // -----------------------------------------------------------------------
  // Contract terms
  // -----------------------------------------------------------------------
  contractTerms: [
    {
      id: "AVTAL-001",
      clause: "Prisreglering",
      description:
        "Priser är fasta under ramavtalets första 12 månader. Därefter tillåts indexreglering enligt SCB:s tjänsteprisindex.",
      riskLevel: "lag",
      flagged: false,
      source: { kind: "file", documentName: BIL2, page: 5 } satisfies SourceRef,
      frameworkDeviation: {
        standard: "ABK 09",
        deviationDescription:
          "ABK 09 §6.1 saknar indexreglering som standard — indexklausul är ett tillägg i detta avtal.",
      },
      snippet: snippet(
        "doc-003",
        BIL2,
        5,
        "Avtalade timpriser är fasta och oförändrade under ramavtalets första tolv (12) månader från avtalstecknande. Därefter får priserna regleras en gång per år i enlighet med SCB:s tjänsteprisindex (TPI).",
        "2.1 Prisreglering\nAvtalade timpriser är fasta och oförändrade under ramavtalets första tolv (12) månader från avtalstecknande. Därefter får priserna regleras en gång per år i enlighet med SCB:s tjänsteprisindex (TPI). Prisreglering får ej överstiga faktisk indexförändring."
      ),
    },
    {
      id: "AVTAL-002",
      clause: "Skadestånd och ansvarsbegränsning",
      description:
        "Leverantörens totalansvar begränsas till 50% av det sammanlagda avtalsvärdet under de senaste 12 månader. Undantag görs för uppsåtlig skada.",
      riskLevel: "medel",
      flagged: false,
      source: { kind: "file", documentName: BIL2, page: 8 } satisfies SourceRef,
      snippet: snippet(
        "doc-003",
        BIL2,
        8,
        "Leverantörens sammanlagda skadeståndsansvar under detta avtal är begränsat till ett belopp motsvarande femtio procent (50 %) av det totala avtalsvärdet under de senaste tolv (12) månaderna.",
        "3.2 Ansvarsbegränsning\nLeverantörens sammanlagda skadeståndsansvar under detta avtal är begränsat till ett belopp motsvarande femtio procent (50 %) av det totala avtalsvärdet under de senaste tolv (12) månaderna. Begränsningen gäller inte vid uppsåt eller grov oaktsamhet."
      ),
    },
    {
      id: "AVTAL-003",
      clause: "Immateriella rättigheter",
      description:
        "Allt material, kod och dokumentation som produceras inom uppdraget tillfaller Region Stockholm med full äganderätt.",
      riskLevel: "hog",
      flagged: true,
      source: { kind: "file", documentName: BIL2, page: 11 } satisfies SourceRef,
      frameworkDeviation: {
        standard: "ABK 09",
        deviationDescription:
          "ABK 09 §6.4 ger normalt leverantören upphovsrätt till handlingar — fullständig äganderättsöverlåtelse är ett signifikant avsteg.",
      },
      snippet: snippet(
        "doc-003",
        BIL2,
        11,
        "Samtliga immateriella rättigheter, inklusive upphovsrätt, till allt material, källkod, dokumentation och övriga leverabler som produceras inom ramen för uppdraget övergår till Region Stockholm vid leverans.",
        "4.1 Immateriella rättigheter\nSamtliga immateriella rättigheter, inklusive upphovsrätt, till allt material, källkod, dokumentation och övriga leverabler som produceras inom ramen för uppdraget övergår till Region Stockholm vid leverans utan ytterligare ersättning."
      ),
    },
    {
      id: "AVTAL-004",
      clause: "Konkurrensklausul",
      description:
        "Konsulter som arbetat på uppdrag hos Region Stockholm får ej anställas av beställaren under avtalstiden och 12 månader därefter.",
      riskLevel: "hog",
      flagged: true,
      source: { kind: "file", documentName: BIL2, page: 13 } satisfies SourceRef,
      frameworkDeviation: {
        standard: "ABK 09",
        deviationDescription:
          "ABK 09 innehåller inga rekryteringsförbud — detta är ett avtalstillägg med avtalsvite om 6 månadslöner per konsult.",
      },
      snippet: snippet(
        "doc-003",
        BIL2,
        13,
        "Beställaren förbinder sig att inte anställa, anlita eller på annat sätt engagera konsulter som utfört uppdrag enligt detta avtal under avtalstiden och tolv (12) månader efter avtalets upphörande.",
        "4.3 Rekryteringsförbud\nBeställaren förbinder sig att inte anställa, anlita eller på annat sätt engagera konsulter som utfört uppdrag enligt detta avtal under avtalstiden och tolv (12) månader efter avtalets upphörande. Brott mot denna bestämmelse medför en avtalsvite om sex (6) månadslöner per konsult."
      ),
    },
    {
      id: "AVTAL-005",
      clause: "Uppsägningstid",
      description:
        "Avtalet kan sägas upp med 6 månaders varsel från båda parter. Beställaren kan säga upp enskilda avropsavtal med 30 dagars varsel.",
      riskLevel: "lag",
      flagged: false,
      source: { kind: "file", documentName: BIL2, page: 16 } satisfies SourceRef,
      snippet: snippet(
        "doc-003",
        BIL2,
        16,
        "Ramavtalet kan av vardera parten sägas upp med sex (6) månaders skriftligt varsel. Enskilda avropsavtal kan sägas upp av beställaren med trettio (30) dagars skriftligt varsel.",
        "5.1 Avtalstid och uppsägning\nRamavtalet kan av vardera parten sägas upp med sex (6) månaders skriftligt varsel. Enskilda avropsavtal kan sägas upp av beställaren med trettio (30) dagars skriftligt varsel utan angivande av skäl."
      ),
    },
    {
      id: "AVTAL-006",
      clause: "SLA och servicenivåer",
      description:
        "Svarstid vid incidenter: Kritisk = 2h, Hög = 8h, Normal = 3 arbetsdagar. Viten utgår vid upprepade SLA-brott.",
      riskLevel: "medel",
      flagged: false,
      source: { kind: "file", documentName: BIL2, page: 20 } satisfies SourceRef,
      snippet: snippet(
        "doc-003",
        BIL2,
        20,
        "Svarstider för incidenter: Kritisk prioritet: 2 timmar. Hög prioritet: 8 arbetstimmar. Normal prioritet: 3 arbetsdagar. Vid upprepade SLA-brott (minst 3 under en 30-dagarsperiod) utgår vite.",
        "6.2 Servicenivåer (SLA)\nSvarstider för incidenter: Kritisk prioritet: 2 timmar. Hög prioritet: 8 arbetstimmar. Normal prioritet: 3 arbetsdagar. Vid upprepade SLA-brott (minst 3 under en 30-dagarsperiod) utgår vite om 0,5 % av månadsarvodet per brott."
      ),
    },
    {
      id: "AVTAL-007",
      clause: "Underleverantörer",
      description:
        "Anlitande av underleverantörer kräver skriftligt godkännande från beställaren i varje enskilt fall.",
      riskLevel: "medel",
      flagged: false,
      source: { kind: "file", documentName: BIL2, page: 22 } satisfies SourceRef,
      snippet: snippet(
        "doc-003",
        BIL2,
        22,
        "Leverantören får inte anlita underleverantörer för utförande av uppdrag inom ramen för detta avtal utan beställarens skriftliga godkännande i varje enskilt fall.",
        "7.1 Underleverantörer\nLeverantören får inte anlita underleverantörer för utförande av uppdrag inom ramen för detta avtal utan beställarens skriftliga godkännande i varje enskilt fall. Ansökan om godkännande ska lämnas minst 10 arbetsdagar i förväg."
      ),
    },
    {
      id: "AVTAL-008",
      clause: "Dataskydd och GDPR",
      description:
        "Leverantören ska teckna personuppgiftsbiträdesavtal och säkerställa att behandling sker inom EU/EES.",
      riskLevel: "lag",
      flagged: false,
      source: { kind: "file", documentName: BIL2, page: 25 } satisfies SourceRef,
      snippet: snippet(
        "doc-003",
        BIL2,
        25,
        "Leverantören ska teckna ett personuppgiftsbiträdesavtal (PUB-avtal) i enlighet med GDPR och säkerställa att all behandling av personuppgifter sker inom EU/EES.",
        "8.1 Dataskydd\nLeverantören ska teckna ett personuppgiftsbiträdesavtal (PUB-avtal) i enlighet med GDPR och säkerställa att all behandling av personuppgifter sker inom EU/EES. Dataöverföring till tredjeland är inte tillåten utan beställarens skriftliga medgivande."
      ),
    },
  ],

  // -----------------------------------------------------------------------
  // Critical dates
  // -----------------------------------------------------------------------
  criticalDates: [
    {
      id: "DATUM-001",
      event: "Sista dag för frågor och förtydliganden",
      date: "2025-12-05",
      daysRemaining: 15,
      urgency: "varning",
      snippet: snippet(
        "doc-001",
        FRF,
        32,
        "Frågor och begäran om förtydliganden ska vara beställaren tillhanda senast 2025-12-05.",
        "8.2 Kommunikation under anbudstiden\nFrågor och begäran om förtydliganden ska vara beställaren tillhanda senast 2025-12-05 kl. 17:00. Frågor mottagna efter detta datum besvaras ej."
      ),
    },
    {
      id: "DATUM-002",
      event: "Anbudstidens utgång",
      date: "2025-12-19",
      daysRemaining: 29,
      urgency: "normal",
      snippet: snippet(
        "doc-001",
        FRF,
        33,
        "Anbud ska vara beställaren tillhanda senast 2025-12-19 klockan 14:00.",
        "8.3 Inlämning\nAnbud ska vara beställaren tillhanda senast 2025-12-19 klockan 14:00. Anbud som inkommer efter angiven tid och datum kommer att förkastas."
      ),
    },
    {
      id: "DATUM-003",
      event: "Tilldelningsbeslut meddelas",
      date: "2026-01-16",
      daysRemaining: 57,
      urgency: "normal",
    },
    {
      id: "DATUM-004",
      event: "Avtalsteckning (planerat)",
      date: "2026-02-03",
      daysRemaining: 75,
      urgency: "normal",
    },
    {
      id: "DATUM-005",
      event: "Ramavtalsstart",
      date: "2026-03-01",
      daysRemaining: 101,
      urgency: "normal",
    },
    {
      id: "DATUM-006",
      event: "Obligatorisk leverantörspresentation",
      date: "2025-11-28",
      daysRemaining: 8,
      urgency: "kritisk",
      snippet: snippet(
        "doc-001",
        FRF,
        35,
        "Deltagande i leverantörspresentation är obligatoriskt och utgör en kvalificeringskontroll. Datum: 2025-11-28.",
        "8.5 Leverantörspresentation\nDeltagande i leverantörspresentation är obligatoriskt och utgör en kvalificeringskontroll. Leverantörer som ej deltar diskvalificeras. Datum: 2025-11-28, kl. 09:00–16:00, plats: Region Stockholms huvudkontor."
      ),
    },
  ],

  // -----------------------------------------------------------------------
  // Requirement matrix (step 3) — now uses SourceEvidence
  // -----------------------------------------------------------------------
  requirementMatrix: [
    {
      requirementId: "SKA-001",
      requirement: "F-skattsedel och momsregistrering",
      status: "uppfyllt",
      gap: "",
      evidence: evidence(
        "Bolagsverket-utdrag daterat 2025-10-15 bekräftar aktiv F-skatt. Skatteverkets register visar löpande momsregistrering sedan 2019. Kravet är uppfyllt utan reservationer.",
        [
          snippet(
            "doc-001",
            FRF,
            7,
            "Leverantören ska inneha F-skattsedel och vara registrerad för mervärdesskatt (moms) vid tidpunkten för anbudslämning.",
            "3.1 Obligatoriska krav\nLeverantören ska inneha F-skattsedel och vara registrerad för mervärdesskatt (moms) vid tidpunkten för anbudslämning."
          ),
        ],
        "saker",
        { status: "verified", checkedAt: "2025-11-20T10:00:00Z", checkedBy: "ai" }
      ),
    },
    {
      requirementId: "SKA-002",
      requirement: "Minst 5 anställda konsulter med IT-kompetens",
      status: "uppfyllt",
      gap: "",
      evidence: evidence(
        "HR-registret visar 12 fast anställda konsulter med dokumenterad IT-kompetens per 2025-11-01. Samtliga uppfyller kravet på minst 2 års yrkeserfarenhet. Kravet är uppfyllt med god marginal.",
        [
          snippet(
            "doc-001",
            FRF,
            8,
            "Leverantören ska vid anbudstillfället ha minst fem (5) fast anställda konsulter med dokumenterad IT-kompetens.",
            "3.2 Kapacitetskrav\nLeverantören ska vid anbudstillfället ha minst fem (5) fast anställda konsulter med dokumenterad IT-kompetens."
          ),
        ],
        "saker",
        { status: "verified", checkedAt: "2025-11-20T10:01:00Z", checkedBy: "ai" }
      ),
    },
    {
      requirementId: "SKA-003",
      requirement: "Dokumenterad erfarenhet agil projektledning",
      status: "uppfyllt",
      gap: "",
      evidence: evidence(
        "CV-databasen visar 3 certifierade Scrum Masters (certifikat daterade 2023–2025) och 1 SAFe Program Consultant. Samtliga certifikat är aktiva. Kravet på minst 1 konsult med agil kompetens är uppfyllt med bred marginal.",
        [
          snippet(
            "doc-002",
            BIL1,
            4,
            "Minst en (1) konsult i leverantörens organisation ska ha dokumenterad och verifierbar erfarenhet av agil projektledning, specifikt Scrum eller SAFe.",
            "2.1 Specifika kompetensskrav\nMinst en (1) konsult ska ha dokumenterad erfarenhet av agil projektledning."
          ),
          librarySnippet(
            "cv-pool-2026",
            "Konsult-CV-bibliotek",
            CV_POOL,
            "Anna Andersson — Certifierad Scrum Master (Scrum Alliance, 2024). Erik Svensson — SAFe Program Consultant (Scaled Agile, 2023). Maria Bergström — Certifierad Scrum Master (Scrum Alliance, 2025).",
            "Agila certifieringar i CV-poolen — 3 Scrum Masters + 1 SAFe PC aktiva per 2025-11-01.",
            3
          ),
        ],
        "saker",
        { status: "verified", checkedAt: "2025-11-20T10:02:00Z", checkedBy: "ai" }
      ),
    },
    {
      requirementId: "SKA-004",
      requirement: "Uppdragsstart inom 10 arbetsdagar",
      status: "uppfyllt",
      gap: "",
      evidence: evidence(
        "Den operativa handboken (v3) beskriver en onboardingprocess om 5–7 arbetsdagar. Processen inkluderar behörighetskontroll, utrustning och introduktion. Kravet om 10 dagar uppfylls med marginal.",
        [
          snippet(
            "doc-001",
            FRF,
            12,
            "Leverantören ska kunna påbörja leverans av beställda konsulttjänster inom tio (10) arbetsdagar från datum för avropsbekräftelse.",
            "4.3 Tillgänglighet\nLeverantören ska kunna påbörja leverans inom tio (10) arbetsdagar."
          ),
        ],
        "saker",
        { status: "verified", checkedAt: "2025-11-20T10:03:00Z", checkedBy: "ai" }
      ),
    },
    {
      requirementId: "SKA-005",
      requirement: "ISO 27001 eller motsvarande",
      status: "delvis",
      gap: "Certifieringsaudit planerad till jan 2026 – risk om certifikat ej hinner utfärdas före anbudslämning 2025-12-19.",
      evidence: evidence(
        "Intern ISMS-policy finns och är dokumenterad. ISO 27001-certifieringsaudit är bokad hos DNV till januari 2026. Anbudet ska lämnas 2025-12-19 — certifikatet kan inte uppvisas vid anbudstillfället. Kravtexten specificerar att certifikat ska bifogas anbudet, vilket skapar en direkt konflikt med vår tidslinje.",
        [
          snippet(
            "doc-002",
            BIL1,
            6,
            "Certifikatet ska vara utfärdat av ett ackrediterat certifieringsorgan. Giltigt certifikat eller likvärdigt intyg ska bifogas anbudet.",
            "2.3 Informationssäkerhet\nLeverantören ska ha en certifierad informationssäkerhetspolicy enligt ISO/IEC 27001 eller motsvarande erkänd standard. Giltigt certifikat eller likvärdigt intyg ska bifogas anbudet."
          ),
        ],
        "osaker",
        {
          status: "contradicted",
          checkedAt: "2025-11-20T10:05:00Z",
          checkedBy: "ai",
          notes:
            "Konflikt med RISK-001: kravtexten kräver certifikat vid anbudslämning men certifieringsaudit är bokad till januari 2026 — efter sista anbudsdag.",
          conflicts: [
            {
              withClaimId: "RISK-001",
              description:
                "Krav SKA-005 är markerat 'delvis' men RISK-001 identifierar detta som showstopper — certifikat kan inte uppvisas vid anbudslämning 2025-12-19.",
              severity: "high",
            },
          ],
        }
      ),
    },
    {
      requirementId: "SKA-006",
      requirement: "Svenska i tal och skrift",
      status: "uppfyllt",
      gap: "",
      evidence: evidence(
        "HR-registret visar att samtliga 12 konsulter antingen är svenska modersmålstalare eller har dokumenterat C2-intyg i svenska. Kravet uppfylls av hela konsultpoolen.",
        [
          snippet(
            "doc-001",
            FRF,
            9,
            "Samtliga konsulter som arbetar inom ramen för ramavtalet ska behärska svenska i tal och skrift på minst nivå C1 enligt CEFR.",
            "3.3 Språkkrav\nSamtliga konsulter ska behärska svenska på minst C1-nivå."
          ),
        ],
        "saker",
        { status: "verified", checkedAt: "2025-11-20T10:06:00Z", checkedBy: "ai" }
      ),
    },
    {
      requirementId: "SKA-007",
      requirement: "Acceptera villkor utan modifikationer",
      status: "obekraftat",
      gap: "Konkurrensklausul (s. 13) och IP-klausul (s. 11) kräver intern juridisk godkännandeprocess — svar förväntas ej förrän vecka 49.",
      evidence: evidence(
        "Avtalsvillkoren har skickats till juridisk avdelning för granskning. Konkurrensklausulen (12 månaders rekryteringsförbud) och IP-klausulen (full äganderättsöverlåtelse) är flaggade som potentiellt problematiska. Juridisk avdelning har bekräftat mottagning men inte lämnat utlåtande. Kravet kan inte bekräftas som uppfyllt utan juridiskt klartecken.",
        [
          snippet(
            "doc-003",
            BIL2,
            2,
            "Leverantören ska utan reservation acceptera och underteckna de allmänna avtalsvillkor som framgår av denna bilaga. Inga avvikelser, tillägg eller förtydliganden accepteras.",
            "1.1 Avtalets omfattning\nLeverantören ska utan reservation acceptera avtalsvillkoren. Inga avvikelser accepteras."
          ),
          snippet(
            "doc-003",
            BIL2,
            13,
            "Beställaren förbinder sig att inte anställa, anlita eller på annat sätt engagera konsulter som utfört uppdrag enligt detta avtal under avtalstiden och tolv (12) månader efter avtalets upphörande.",
            "4.3 Rekryteringsförbud\nBeställaren förbinder sig att inte anställa konsulter under avtalstiden och 12 månader därefter."
          ),
        ],
        "osaker",
        {
          status: "unverified",
          checkedAt: "2025-11-20T10:07:00Z",
          checkedBy: "ai",
          notes: "Inväntar juridisk avdelnings granskning av konkurrensklausul och IP-klausul.",
        }
      ),
    },
    {
      requirementId: "SKA-008",
      requirement: "2 referensuppdrag offentlig sektor, 3 år",
      status: "uppfyllt",
      gap: "",
      evidence: evidence(
        "Referenslistan innehåller Göteborg Stad (2023–2024, IT-systemintegration, 1,2 MSEK) och Trafikverket (2024–pågående, agil systemutveckling, >500 KSEK). Båda uppfyller kraven på offentlig beställare och minsta kontraktsvärde.",
        [
          snippet(
            "doc-001",
            FRF,
            11,
            "Leverantören ska redovisa minst två (2) genomförda referensuppdrag hos offentliga beställare utförda under de senaste tre (3) åren. Uppdragen ska ha haft ett kontraktsvärde om minst 500 000 SEK vardera.",
            "3.5 Erfarenhetskrav\nMinst 2 referensuppdrag hos offentliga beställare de senaste 3 åren, minst 500 000 SEK vardera."
          ),
        ],
        "saker",
        { status: "verified", checkedAt: "2025-11-20T10:08:00Z", checkedBy: "ai" }
      ),
    },
    {
      requirementId: "SKA-009",
      requirement: "Konsulter med säkerhetsgodkännande nivå C",
      status: "delvis",
      gap: "Kapacitetsbrist om fler än 2 säkerhetsklassade konsulter behövs parallellt. 3 ytterligare konsulter är under prövning — utfall oklart.",
      evidence: evidence(
        "Säkerhetsregistret visar 2 konsulter med aktivt nivå C-godkännande per 2025-11-01. Ytterligare 3 konsulter har pågående säkerhetsprövning initierad i september 2025. Kravtexten anger att leverantören 'ska kunna tillhandahålla' — det råder osäkerhet kring om 2 godkända konsulter är tillräckligt om beställaren avroper fler parallella uppdrag.",
        [
          snippet(
            "doc-002",
            BIL1,
            8,
            "För uppdrag som involverar känslig information ska leverantören kunna tillhandahålla konsulter med säkerhetsgodkännande av lägst nivå C i enlighet med säkerhetsskyddslagen.",
            "2.4 Säkerhetsgodkännande\nLeverantören ska kunna tillhandahålla konsulter med säkerhetsgodkännande av lägst nivå C."
          ),
          librarySnippet(
            "cv-pool-2026",
            "Konsult-CV-bibliotek",
            CV_POOL,
            "Jonas Karlsson — Säkerhetsgodkänd nivå C (aktiv, godkänd 2023). Petra Holm — Säkerhetsgodkänd nivå C (aktiv, godkänd 2024). Lars Ek, Camilla Nord, Tobias Ågren — säkerhetsprövning initierad september 2025, beslut inväntas.",
            "Säkerhetsgodkännanden i CV-poolen — 2 aktiva nivå C, 3 under prövning per 2025-11-01.",
            7
          ),
        ],
        "osaker",
        {
          status: "partially_verified",
          checkedAt: "2025-11-20T10:09:00Z",
          checkedBy: "ai",
          notes:
            "Kapacitetsfrågan är ej helt klarlagd — beroende av hur beställaren definierar 'kunna tillhandahålla'.",
        }
      ),
    },
    {
      requirementId: "SKA-010",
      requirement: "Månatliga statusrapporter",
      status: "uppfyllt",
      gap: "",
      evidence: evidence(
        "Befintlig rapporteringsmall (v2) innehåller samtliga av beställaren efterfrågade delar: status per uppdrag, avvikelser mot plan, riskbild och förbrukning mot budget. Mallen genereras automatiskt från projekthanteringssystemet.",
        [
          snippet(
            "doc-003",
            BIL2,
            14,
            "Leverantören ska månadsvis, senast den 5:e i påföljande månad, lämna en skriftlig statusrapport. Rapporten ska innehålla status, avvikelser, riskbild och förbrukning mot budget.",
            "5.2 Rapportering\nMånatlig statusrapport senast den 5:e i påföljande månad med status, avvikelser, riskbild och förbrukning."
          ),
        ],
        "saker",
        { status: "verified", checkedAt: "2025-11-20T10:10:00Z", checkedBy: "ai" }
      ),
    },
  ],

  // -----------------------------------------------------------------------
  // Risks (step 3) — now uses SourceEvidence
  // -----------------------------------------------------------------------
  risks: [
    {
      id: "RISK-001",
      title: "ISO 27001-certifikat saknas vid anbudslämning",
      description:
        "Certifieringsaudit är planerad till januari 2026 men ramavtalets kravformulering kräver att giltigt certifikat bifogas anbudet (sista dag 2025-12-19). Risk att anbudet diskvalificeras.",
      severity: 5,
      probability: 3,
      isShowstopper: true,
      mitigationNote:
        "Begär förtydligande från beställaren om certifikatet kan uppvisas vid avtalsteckning snarare än anbudslämning. Undersök om pågående certifieringsprocess + revisionsplan är acceptabelt.",
      evidence: evidence(
        "Kravspecifikationen (BIL1, s.6) anger explicit att 'Giltigt certifikat eller likvärdigt intyg ska bifogas anbudet'. Anbudssista är 2025-12-19 och certifieringsaudit är bokad till januari 2026. Tidsglapet är 3–6 veckor. Beställarens svar på fråga om alternativt bevis inväntas (frågestopp 2025-12-05).",
        [
          snippet(
            "doc-002",
            BIL1,
            6,
            "Giltigt certifikat eller likvärdigt intyg ska bifogas anbudet. Certifikatet ska vara utfärdat av ett ackrediterat certifieringsorgan.",
            "2.3 Informationssäkerhet\nLeverantören ska ha en certifierad informationssäkerhetspolicy enligt ISO/IEC 27001. Giltigt certifikat ska bifogas anbudet."
          ),
          librarySnippet(
            "standards",
            "Standardbibliotek",
            "ISO/IEC 27001:2022 — Annex A",
            "Certifieringsorganet ska genomföra en Stage 1- och Stage 2-revision. Utfärdande av certifikat sker normalt 2–4 veckor efter godkänd Stage 2-revision.",
            "ISO/IEC 27001:2022 Annex A — certifieringsprocess: Stage 1 (dokumentgranskning) följt av Stage 2 (platsgranskning). Certifikatet utfärdas av ackrediterat organ efter godkänt revisionsutfall.",
            18
          ),
        ],
        "osaker",
        {
          status: "contradicted",
          checkedAt: "2025-11-20T10:20:00Z",
          checkedBy: "ai",
          notes:
            "Direkt konflikt med SKA-005 (delvis uppfyllt): certifikat kan inte uppvisas vid anbudslämning 2025-12-19.",
          conflicts: [
            {
              withClaimId: "SKA-005",
              description:
                "SKA-005 är markerat 'delvis uppfyllt' men kravtexten lämnar inget tolkningsutrymme — certifikat krävs vid anbudslämning.",
              severity: "high",
            },
          ],
        }
      ),
    },
    {
      id: "RISK-002",
      title: "Konkurrensklausul – juridisk granskning krävs",
      description:
        "Klausulen om 12 månaders rekryteringsförbud efter avtalstid kan vara juridiskt tvivelaktig och strida mot interna HR-policyer.",
      severity: 4,
      probability: 4,
      isShowstopper: true,
      mitigationNote:
        "Eskalera till juridisk avdelning omedelbart. Begär undantag eller klarläggande från beställaren via frågerundan (sista dag 2025-12-05).",
      evidence: evidence(
        "Bilaga 2, s.13 innehåller ett dubbelriktat rekryteringsförbud som i praktiken begränsar beställarens möjlighet att anställa våra konsulter och omvänt. Klausulen kan strida mot avtalslagen §36 om oskälighet. Avtalsvitet på 6 månadslöner per konsult är ovanligt högt. Intern HR-policy tillåter inte denna typ av klausul utan styrelsebeslut.",
        [
          snippet(
            "doc-003",
            BIL2,
            13,
            "Beställaren förbinder sig att inte anställa, anlita eller på annat sätt engagera konsulter som utfört uppdrag enligt detta avtal under avtalstiden och tolv (12) månader efter avtalets upphörande. Brott mot denna bestämmelse medför en avtalsvite om sex (6) månadslöner per konsult.",
            "4.3 Rekryteringsförbud\nBeställaren förbinder sig att inte anställa konsulter under avtalstiden och 12 månader efter avtalets upphörande. Avtalsvite: 6 månadslöner per konsult."
          ),
          librarySnippet(
            "legal",
            "Juridisk referens",
            "Avtalslagen (1915:218) — §36 Jämkning av oskäliga avtalsvillkor",
            "Avtalsvillkor får jämkas eller lämnas utan avseende, om villkoret är oskäligt med hänsyn till avtalets innehåll, omständigheterna vid avtalets tillkomst, senare inträffade förhållanden och omständigheterna i övrigt.",
            "Avtalslagen §36 — generalklausulen om oskälighet. Tillämplig på klausuler med ovanligt höga viten eller oproportionerliga begränsningar av avtalsparten."
          ),
        ],
        "osaker",
        {
          status: "partially_verified",
          checkedAt: "2025-11-20T10:21:00Z",
          checkedBy: "ai",
          notes: "Juridisk avdelning har flaggat klausulen men inte lämnat slutgiltigt utlåtande.",
        }
      ),
    },
    {
      id: "RISK-003",
      title: "Full äganderätt till IP kan begränsa återanvändning",
      description:
        "Om all kod och dokumentation tillfaller beställaren kan vi inte återanvända ramverk och mallar i andra uppdrag.",
      severity: 3,
      probability: 5,
      isShowstopper: false,
      mitigationNote:
        "Förhandla om undantag för generiska komponenter och open-source-baserade lösningar i frågerundan.",
      evidence: evidence(
        "Bilaga 2, s.11 ger beställaren full äganderätt till 'allt material, källkod, dokumentation och övriga leverabler'. Formuleringen 'övriga leverabler' är bred och kan tolkas inkludera interna ramverk och återanvändningsbara komponenter. Sannolikheten bedöms som hög (5/5) eftersom klausulen är formulerad utan undantag.",
        [
          snippet(
            "doc-003",
            BIL2,
            11,
            "Samtliga immateriella rättigheter, inklusive upphovsrätt, till allt material, källkod, dokumentation och övriga leverabler som produceras inom ramen för uppdraget övergår till Region Stockholm vid leverans.",
            "4.1 Immateriella rättigheter\nAlla immateriella rättigheter till leverabler övergår till Region Stockholm utan ytterligare ersättning."
          ),
        ],
        "saker",
        { status: "verified", checkedAt: "2025-11-20T10:22:00Z", checkedBy: "ai" }
      ),
    },
    {
      id: "RISK-004",
      title: "Begränsad pool med säkerhetsklassade konsulter",
      description:
        "Nuvarande kapacitet om 2 godkända konsulter kan vara otillräcklig om beställaren avroper parallella säkerhetsklassade uppdrag.",
      severity: 3,
      probability: 3,
      isShowstopper: false,
      mitigationNote:
        "Sätt ett volymtak i anbudet och påbörja godkännandeprocess för 3 ytterligare konsulter omgående.",
      evidence: evidence(
        "Kravspecifikationen (BIL1, s.8) anger att leverantören 'ska kunna tillhandahålla' konsulter med nivå C-godkännande. Nuvarande kapacitet är 2 konsulter. Beställarens historiska avropsmönster (från liknande ramavtal) visar behov av upp till 4 säkerhetsklassade konsulter parallellt.",
        [
          snippet(
            "doc-002",
            BIL1,
            8,
            "För uppdrag som involverar känslig information ska leverantören kunna tillhandahålla konsulter med säkerhetsgodkännande av lägst nivå C i enlighet med säkerhetsskyddslagen.",
            "2.4 Säkerhetsgodkännande\nLeverantören ska kunna tillhandahålla konsulter med säkerhetsgodkännande nivå C."
          ),
        ],
        "osaker",
        { status: "partially_verified", checkedAt: "2025-11-20T10:23:00Z", checkedBy: "ai" }
      ),
    },
    {
      id: "RISK-005",
      title: "Prispresning vid hög viktning",
      description:
        "40% viktning på pris kan driva mot oacceptabelt låg lönsamhet om konkurrenterna prisar aggressivt.",
      severity: 2,
      probability: 4,
      isShowstopper: false,
      mitigationNote:
        "Kalkylera break-even och sätt ett golv. Kompensera med starka poäng på kompetens (30%).",
      evidence: evidence(
        "Prisbilagan (BIL3) visar att priskriteriet har 40% vikt och utvärderas mot volymvägda timpriser. Volymantagandena i BIL3 är konservativa men breda — en konkurrent som prisar 15% lägre kan kompensera väsentligt. Marginalkalkyl visar att 15% prisskärning reducerar täckningsbidraget till icke-acceptabel nivå.",
        [
          snippet(
            "doc-001",
            FRF,
            18,
            "Priskriteriet viktas med 40 % av det totala anbudsvärdet och utvärderas baserat på angivna timpriser per konsultprofil multiplicerat med uppskattade avropade volymer.",
            "6.1 Priskriterium\nPriset viktas med 40 % och utvärderas mot volymvägda timpriser enligt Bilaga 3."
          ),
        ],
        "saker",
        { status: "verified", checkedAt: "2025-11-20T10:24:00Z", checkedBy: "ai" }
      ),
    },
  ],

  // -----------------------------------------------------------------------
  // Scorecard dimensions (step 4) — now has evidence
  // -----------------------------------------------------------------------
  scorecardDimensions: [
    {
      id: "dim-01",
      label: "Kravuppfyllnad",
      score: 78,
      description: "8 av 10 ska-krav uppfyllda eller delvis uppfyllda",
      evidence: evidence(
        "Av 10 ska-krav är 7 uppfyllda (SKA-001–004, 006, 008, 010), 2 delvis uppfyllda (SKA-005, SKA-009) och 1 obekräftat (SKA-007). De delvis uppfyllda kraven har tydliga mitigeringsvägar. Poängen 78 reflekterar 70% viktad kravuppfyllnad plus 8 procentenheter för mitigeringspotential.",
        [
          snippet(
            "doc-002",
            BIL1,
            6,
            "Giltigt certifikat eller likvärdigt intyg ska bifogas anbudet.",
            "2.3 Informationssäkerhet — certifikat krävs vid anbudslämning."
          ),
          snippet(
            "doc-002",
            BIL1,
            8,
            "Leverantören ska kunna tillhandahålla konsulter med säkerhetsgodkännande av lägst nivå C.",
            "2.4 Säkerhetsgodkännande — kapacitetskrav oklart."
          ),
        ],
        "osaker",
        { status: "partially_verified", checkedAt: "2025-11-20T11:00:00Z", checkedBy: "ai" }
      ),
    },
    {
      id: "dim-02",
      label: "Riskprofil",
      score: 52,
      description: "2 showstoppers identifierade – kräver omedelbar åtgärd",
      evidence: evidence(
        "Riskmatrisen innehåller 5 identifierade risker. RISK-001 (ISO-certifikat) och RISK-002 (konkurrensklausul) är showstoppers med sammanlagd riskpoäng 15+16=31. Återstående 3 risker (RISK-003–005) är hanterbara. Poängen 52 speglar att showstoppers finns men inte är omöjliga att lösa inom tillgänglig tidsram.",
        [
          snippet(
            "doc-002",
            BIL1,
            6,
            "Giltigt certifikat eller likvärdigt intyg ska bifogas anbudet.",
            "Showstopper RISK-001 grundas i detta krav."
          ),
          snippet(
            "doc-003",
            BIL2,
            13,
            "Brott mot denna bestämmelse medför en avtalsvite om sex (6) månadslöner per konsult.",
            "Showstopper RISK-002 grundas i konkurrensklausulen."
          ),
        ],
        "osaker",
        { status: "partially_verified", checkedAt: "2025-11-20T11:01:00Z", checkedBy: "ai" }
      ),
    },
    {
      id: "dim-03",
      label: "Tidshållbarhet",
      score: 85,
      description: "Gott om tid till anbudslämning, leverantörspresentation kritisk",
      evidence: evidence(
        "Anbudssista är 2025-12-19 — 29 dagar kvar. Intern processtid för anbud av denna typ är 10–14 dagar. God marginal finns. Kritisk punkt är leverantörspresentationen 2025-11-28 (8 dagar) som är obligatorisk kvalificeringskontroll.",
        [
          snippet(
            "doc-001",
            FRF,
            33,
            "Anbud ska vara beställaren tillhanda senast 2025-12-19 klockan 14:00.",
            "8.3 Inlämning — sista anbudsdag."
          ),
          snippet(
            "doc-001",
            FRF,
            35,
            "Deltagande i leverantörspresentation är obligatoriskt och utgör en kvalificeringskontroll.",
            "8.5 Leverantörspresentation — obligatorisk."
          ),
        ],
        "saker",
        { status: "verified", checkedAt: "2025-11-20T11:02:00Z", checkedBy: "ai" }
      ),
    },
    {
      id: "dim-04",
      label: "Resursmatchning",
      score: 70,
      description: "God kapacitet generellt, begränsat för säkerhetsklassade roller",
      evidence: evidence(
        "Konsultpoolen med 12 anställda ger god allmän kapacitet. 3 certifierade Scrum Masters och 1 SAFe-konsult täcker agil erfarenhet. Begränsningen ligger i säkerhetsklassade roller (max 2 parallellt idag). Volymtaket i anbudet bör sättas till 2 parallella säkerhetsklassade uppdrag tills fler godkänns.",
        [
          snippet(
            "doc-001",
            FRF,
            8,
            "Leverantören ska vid anbudstillfället ha minst fem (5) fast anställda konsulter med dokumenterad IT-kompetens.",
            "3.2 Kapacitetskrav — minst 5 konsulter krävs."
          ),
        ],
        "saker",
        { status: "verified", checkedAt: "2025-11-20T11:03:00Z", checkedBy: "ai" }
      ),
    },
    {
      id: "dim-06",
      label: "Kommersiell attraktivitet",
      score: 80,
      description:
        "Ramavtal ~5–15 MSEK/år med option till 2029, 40% prisvikt ger förhandlingsutrymme",
      evidence: evidence(
        "Upphandlingens volymantaganden (BIL3) indikerar 5–15 MSEK/år i estimerat ramavtalsvärde. Avtalslängd 2 år med option 2+2 år ger ett totalt potentiellt värde om 20–60 MSEK. 40%-prisvikt är högt men hanterbara volymantaganden ger marginalmöjlighet. Indexreglering (SCB TPI) skyddar marginalen över tid.",
        [
          snippet(
            "doc-004",
            BIL3,
            3,
            "Timpriser ska anges per konsultprofil i enlighet med de profiler som definieras i denna bilaga.",
            "Prisbilaga — volymunderlag för kalkyl."
          ),
        ],
        "osaker",
        { status: "partially_verified", checkedAt: "2025-11-20T11:05:00Z", checkedBy: "ai" }
      ),
    },
    {
      id: "dim-05",
      label: "Strategisk passning",
      score: 88,
      description: "Upphandlingen matchar bolagets kärnkompetenser väl",
      evidence: evidence(
        "Upphandlingens fokus på agil IT-leverans, systemutveckling och IT-konsultation matchar bolagets kärnkompetenser och tillväxtstrategi för 2026. Referensportföljen är direkt relevant. Volymantagandena i prisbilagan (BIL3) indikerar ett ramavtal av storleksordning 5–15 MSEK/år — strategiskt intressant.",
        [
          snippet(
            "doc-001",
            FRF,
            19,
            "Kompetens och erfarenhet utvärderas med 30 % vikt baserat på inlämnade CV, certifieringsintyg och redovisade referensuppdrag.",
            "6.2 Kompetens — 30% vikt, direkt relevant för vår profil."
          ),
        ],
        "saker",
        { status: "verified", checkedAt: "2025-11-20T11:04:00Z", checkedBy: "ai" }
      ),
    },
  ],

  // -----------------------------------------------------------------------
  // Recommendation (step 4) — now uses RecommendationReason
  // -----------------------------------------------------------------------
  recommendation: {
    verdict: "go_med_forbehall",
    confidence: 71,
    summary:
      "Analysen visar att bolaget i grunden är väl positionerat för denna upphandling och att de flesta ska-krav kan uppfyllas. Det finns dock två kritiska blockare som måste hanteras skyndsamt: (1) osäkerheten kring ISO 27001-certifikatets tidpunkt och (2) konkurrensklausulen i avtalsvillkoren. Om dessa löses bedöms chansen att vinna upphandlingen som god, givet bolagets starka referensportfölj och kompetensbredd inom agil IT-leverans.",
    reasonsFor: [
      {
        text: "Stark referensportfölj med relevanta offentliga kunder",
        evidence: evidence(
          "Göteborg Stad och Trafikverket är direktrelevanta referenser som väger tungt i 30%-kriteriet för kompetens och erfarenhet (30% vikt). Båda uppdragen är dokumenterade och verifierbara mot erfarenhetskravet, och referenserna adresserar direkt utvärderingskriteriet UTV-002.",
          [
            snippet(
              "doc-001",
              FRF,
              11,
              "Leverantören ska redovisa minst två (2) genomförda referensuppdrag hos offentliga beställare utförda under de senaste tre (3) åren.",
              "3.5 Erfarenhetskrav\nLeverantören ska redovisa minst två (2) genomförda referensuppdrag hos offentliga beställare utförda under de senaste tre (3) åren. Uppdragen ska ha haft ett kontraktsvärde om minst 500 000 SEK vardera."
            ),
            snippet(
              "doc-001",
              FRF,
              19,
              "Kompetens och erfarenhet utvärderas med 30 % vikt baserat på inlämnade CV, certifieringsintyg och redovisade referensuppdrag.",
              "6.2 Kompetens och erfarenhet (30 %)\nKompetens och erfarenhet utvärderas med 30 % vikt baserat på inlämnade CV, certifieringsintyg och redovisade referensuppdrag. Varje konsultprofil poängsätts individuellt och viktas mot den procentuella andelen av estimerade avrop."
            ),
          ],
          "saker",
          { status: "verified", checkedAt: "2025-11-20T12:00:00Z", checkedBy: "ai" }
        ),
      },
      {
        text: "Kompetent pool med agila certifieringar (Scrum, SAFe)",
        evidence: evidence(
          "3 aktiva Scrum Master-certifikat och 1 SAFe Program Consultant överträffar minimikravet. Direkt adresserar UTV-003 (metodkompetens, 15% vikt).",
          [
            snippet(
              "doc-002",
              BIL1,
              4,
              "Minst en (1) konsult ska ha dokumenterad och verifierbar erfarenhet av agil projektledning, specifikt Scrum eller SAFe.",
              "2.1 Kompetensskrav — agil erfarenhet."
            ),
          ],
          "saker",
          { status: "verified", checkedAt: "2025-11-20T12:01:00Z", checkedBy: "ai" }
        ),
      },
      {
        text: "40% priskriterie är förhandlingsbart med rätt kalkyl",
        evidence: evidence(
          "Prisbilagan anger volymantaganden som ger handlingsutrymme. Break-even-analysen visar att vi kan prissätta konkurrenskraftigt och ändå hålla god marginal.",
          [
            snippet(
              "doc-001",
              FRF,
              18,
              "Priskriteriet viktas med 40 % av det totala anbudsvärdet.",
              "6.1 Priskriterium."
            ),
          ],
          "saker",
          { status: "verified", checkedAt: "2025-11-20T12:02:00Z", checkedBy: "ai" }
        ),
      },
      {
        text: "8 av 10 ska-krav uppfylls utan reservationer",
        evidence: evidence(
          "SKA-001–004, 006, 008, 010 är alla fullt uppfyllda med dokumentation. Endast SKA-005 och SKA-009 har partiell uppfyllnad och SKA-007 inväntar juridisk bekräftelse.",
          [
            snippet(
              "doc-001",
              FRF,
              7,
              "Leverantören ska inneha F-skattsedel och vara registrerad för mervärdesskatt.",
              "SKA-001 — fullt uppfyllt."
            ),
          ],
          "saker",
          { status: "verified", checkedAt: "2025-11-20T12:03:00Z", checkedBy: "ai" }
        ),
      },
    ],
    reasonsAgainst: [
      {
        text: "ISO 27001-certifikat saknas vid anbudslämning",
        evidence: evidence(
          "Certifieringsaudit är bokad till januari 2026, men anbudssista är 2025-12-19. Kravtexten är entydig — certifikat ska bifogas anbudet. Detta är en direkt diskvalificeringsrisk.",
          [
            snippet(
              "doc-002",
              BIL1,
              6,
              "Giltigt certifikat eller likvärdigt intyg ska bifogas anbudet. Certifikatet ska vara utfärdat av ett ackrediterat certifieringsorgan.",
              "2.3 Informationssäkerhet."
            ),
          ],
          "osaker",
          {
            status: "contradicted",
            checkedAt: "2025-11-20T12:04:00Z",
            checkedBy: "ai",
            notes: "Showstopper RISK-001.",
          }
        ),
      },
      {
        text: "Konkurrensklausul kräver juridisk utredning",
        evidence: evidence(
          "Klausulen på BIL2, s.13 kombinerar ett 12-månaders rekryteringsförbud med ett avtalsvite på 6 månadslöner per konsult. Intern HR-policy tillåter inte denna typ av klausul utan styrelsebeslut.",
          [
            snippet(
              "doc-003",
              BIL2,
              13,
              "Beställaren förbinder sig att inte anställa konsulter under avtalstiden och tolv (12) månader efter avtalets upphörande. Brott medför avtalsvite om sex (6) månadslöner per konsult.",
              "4.3 Rekryteringsförbud."
            ),
          ],
          "osaker",
          {
            status: "unverified",
            checkedAt: "2025-11-20T12:05:00Z",
            checkedBy: "ai",
            notes: "Inväntar juridisk utredning.",
          }
        ),
      },
      {
        text: "Begränsad kapacitet för säkerhetsklassade uppdrag",
        evidence: evidence(
          "Max 2 konsulter med nivå C-godkännande kan levereras parallellt idag. Om beställaren avroper mer riskerar vi kontraktsbrott.",
          [
            snippet(
              "doc-002",
              BIL1,
              8,
              "Leverantören ska kunna tillhandahålla konsulter med säkerhetsgodkännande av lägst nivå C.",
              "2.4 Säkerhetsgodkännande."
            ),
          ],
          "osaker",
          { status: "partially_verified", checkedAt: "2025-11-20T12:06:00Z", checkedBy: "ai" }
        ),
      },
    ],
    uncertainties: [
      {
        text: "Beställarens tolkning av ISO 27001-kravet (vid anbudslämning vs. avtalstart)",
        evidence: evidence(
          "Frågerundan (sista dag 2025-12-05) ger möjlighet att inhämta förtydligande. Om beställaren accepterar pågående certifieringsprocess + revisionsplan löser detta showstoppern.",
          [
            snippet(
              "doc-001",
              FRF,
              32,
              "Frågor och begäran om förtydliganden ska vara beställaren tillhanda senast 2025-12-05.",
              "8.2 Frågestopp — sista chans att inhämta klarlägganden."
            ),
          ],
          "osaker",
          { status: "unverified", checkedAt: "2025-11-20T12:07:00Z", checkedBy: "ai" }
        ),
      },
      {
        text: "Förhandlingsutrymme på avtalsvillkoren är okänt",
        evidence: evidence(
          "Beställarens formulering 'Inga avvikelser accepteras' är absolut i skrift, men praxis inom offentlig upphandling tillåter ibland förtydliganden. Juridisk avdelning utreder.",
          [
            snippet(
              "doc-003",
              BIL2,
              2,
              "Inga avvikelser, tillägg eller förtydliganden accepteras utan skriftligt medgivande från beställaren.",
              "1.1 Avtalets omfattning."
            ),
          ],
          "osaker",
          { status: "unverified", checkedAt: "2025-11-20T12:08:00Z", checkedBy: "ai" }
        ),
      },
      {
        text: "Konkurrenternas prisnivå är ej känd",
        evidence: evidence(
          "Inga publika signaler om konkurrenters prisnivå finns tillgängliga. Historiska upphandlingar av liknande typ pekar på ett spann om 850–1 150 SEK/timme för senior IT-konsult.",
          [
            snippet(
              "doc-004",
              BIL3,
              3,
              "Timpriser ska anges per konsultprofil i enlighet med de profiler som definieras i denna bilaga.",
              "Prisbilaga — anger format för timpriser."
            ),
          ],
          "osaker",
          { status: "unverified", checkedAt: "2025-11-20T12:09:00Z", checkedBy: "ai" }
        ),
      },
    ],
    actionItems: [
      {
        id: "ACT-001",
        text: "Skicka fråga till beställaren före 2025-12-05: accepteras pågående ISO 27001-certifieringsprocess som likvärdigt intyg istället för utfärdat certifikat?",
        priority: "hog",
        relatedIds: ["SKA-005", "RISK-001"],
      },
      {
        id: "ACT-002",
        text: "Eskalera konkurrensklausul (AVTAL-004) och IP-klausul (AVTAL-003) till juridisk avdelning för granskning — svar krävs senast v. 49 för att hålla anbudsdeadline.",
        priority: "hog",
        relatedIds: ["AVTAL-003", "AVTAL-004", "RISK-002", "SKA-007"],
      },
      {
        id: "ACT-003",
        text: "Initiera säkerhetsklassningsprocess för minst 3 ytterligare konsulter för att öka kapaciteten för parallella säkerhetsklassade avrop.",
        priority: "medel",
        relatedIds: ["SKA-009", "RISK-004"],
      },
      {
        id: "ACT-004",
        text: "Genomför bryt-even-kalkyl mot volymantagandena i BIL3 och sätt prisgolv. Senior IT-konsult historiskt 850–1 150 SEK/h — prissätt i överkant av spannet för att skydda marginal.",
        priority: "medel",
        relatedIds: ["UTV-001", "RISK-005"],
      },
    ],
  },
  roleRequirements: [
    {
      id: "ROLL-001",
      roleName: "Senior IT-arkitekt",
      quantity: 1,
      educationLevel: "Högskolenivå (civilingenjör eller motsvarande)",
      minYearsExperience: 7,
      requiredCertifications: [],
      requiredExperience: [
        "Systemarkitektur för offentlig sektor",
        "Erfarenhet av säkerhetsgodkännandeprocesser",
        "Minst ett referensuppdrag hos offentlig beställare senaste 3 åren",
      ],
      meritingQualifications: ["AWS Solutions Architect", "Azure Solutions Architect", "TOGAF"],
      source: { kind: "file", documentName: BIL1, page: 4 } satisfies SourceRef,
      snippet: snippet(
        "doc-002",
        BIL1,
        4,
        "Minst en (1) konsult ska ha dokumenterad erfarenhet av systemarkitektur och ha arbetat med säkerhetsklassade system.",
        "2.1 Kompetensprofiler — Senior IT-arkitekt"
      ),
    },
    {
      id: "ROLL-002",
      roleName: "Agil projektledare / Scrum Master",
      quantity: 2,
      educationLevel: "Högskolenivå eller dokumenterad erfarenhet",
      minYearsExperience: 4,
      requiredCertifications: ["Certified Scrum Master (CSM) eller SAFe Agilist"],
      requiredExperience: [
        "Ledning av agila projektteam (Scrum/SAFe)",
        "Erfarenhet av offentlig sektors IT-projekt",
      ],
      meritingQualifications: ["SAFe Program Consultant (SPC)", "PMP-certifiering"],
      source: { kind: "file", documentName: BIL1, page: 5 } satisfies SourceRef,
      snippet: snippet(
        "doc-002",
        BIL1,
        5,
        "Minst en (1) konsult ska ha dokumenterad och verifierbar erfarenhet av agil projektledning, specifikt Scrum eller SAFe.",
        "2.1 Specifika kompetensskrav — Agil projektledare"
      ),
    },
    {
      id: "ROLL-003",
      roleName: "Systemutvecklare",
      quantity: 3,
      educationLevel: "Högskolenivå eller YH inom systemutveckling",
      minYearsExperience: 3,
      requiredCertifications: [],
      requiredExperience: [
        "Backend-utveckling (Java, .NET eller Python)",
        "CI/CD och DevOps-praktiker",
        "REST API-design och integration",
      ],
      meritingQualifications: [
        "Molncertifiering (AWS/Azure)",
        "Erfarenhet av vårdinformationssystem",
      ],
      source: { kind: "file", documentName: BIL1, page: 6 } satisfies SourceRef,
      snippet: snippet(
        "doc-002",
        BIL1,
        6,
        "Systemutvecklare ska ha minst tre (3) års dokumenterad erfarenhet inom professionell systemutveckling.",
        "2.2 Kompetensprofil — Systemutvecklare"
      ),
    },
  ],

  bidDeliverables: [
    {
      id: "DEL-001",
      name: "CV per konsult",
      description:
        "CV ska bifogas för samtliga offererade konsulter med utbildning och erfarenhet styrkt.",
      isRequired: true,
      source: { kind: "file", documentName: FRF, page: 24 } satisfies SourceRef,
      snippet: snippet(
        "doc-001",
        FRF,
        24,
        "CV ska bifogas för samtliga erbjudna resurser. CV ska verifiera utbildning och arbetslivserfarenhet för namngivna personer.",
        "7.1 Handlingar som ska ingå i offert — CV"
      ),
    },
    {
      id: "DEL-002",
      name: "Referensuppdrag",
      description:
        "Minst 2 referensuppdrag hos offentliga beställare, senaste 3 åren, minst 500 000 SEK vardera.",
      isRequired: true,
      source: { kind: "file", documentName: FRF, page: 11 } satisfies SourceRef,
      snippet: snippet(
        "doc-001",
        FRF,
        11,
        "Leverantören ska redovisa minst två (2) genomförda referensuppdrag hos offentliga beställare utförda under de senaste tre (3) åren.",
        "3.5 Erfarenhetskrav — referensuppdrag"
      ),
    },
    {
      id: "DEL-003",
      name: "Organisationsplan",
      description: "Organisationsplan över planerad uppdragsorganisation med namn och roller.",
      isRequired: true,
      source: { kind: "file", documentName: FRF, page: 25 } satisfies SourceRef,
    },
    {
      id: "DEL-004",
      name: "Prisbilaga",
      description: "Timpriser per konsultprofil enligt prisbilagan (BIL3). Fasta 12 månader.",
      isRequired: true,
      source: { kind: "file", documentName: BIL3, page: 1 } satisfies SourceRef,
    },
    {
      id: "DEL-005",
      name: "Metodbeskrivning",
      description:
        "Beskrivning av leveransmetodik, kvalitetssäkringsprocess och rapporteringsrutiner.",
      isRequired: true,
      source: { kind: "file", documentName: FRF, page: 26 } satisfies SourceRef,
    },
    {
      id: "DEL-006",
      name: "ISO 27001-certifikat eller likvärdigt intyg",
      description:
        "Giltigt certifikat utfärdat av ackrediterat organ — eller begäran om förtydligande om pågående process accepteras.",
      isRequired: true,
      source: { kind: "file", documentName: BIL1, page: 6 } satisfies SourceRef,
      snippet: snippet(
        "doc-002",
        BIL1,
        6,
        "Giltigt certifikat eller likvärdigt intyg ska bifogas anbudet.",
        "2.3 Informationssäkerhet — certifikat krävs"
      ),
    },
  ],

  procurementOverview: {
    description:
      "Region Stockholm söker en eller flera IT-konsultleverantörer för ett ramavtal med löptid 2026–2028. Upphandlingen avser bred IT-konsultation inom systemutveckling, agil projektledning och teknisk arkitektur för regionens interna digitaliseringsprojekt. Leverantören förväntas ha dokumenterad offentlig sektorerfarenhet och kunna ställa upp konsulter med varierande säkerhetsgodkännandenivå.",
    highlights: [
      "10 ska-krav + 2 meriterande krav",
      "Utvärdering: pris 40 %, kompetens 30 %, metodkompetens 15 %, hållbarhet 15 %",
    ],
    procurementType: "Ramavtal",
    domain: "IT-konsultation",
    client: "Region Stockholm",
    contractStandard: "ABK 09 med avsteg",
    estimatedValue: "5–15 MSEK/år",
    scopeSummary: "Löpande avrop, estimerat 1 200–2 000 h/år",
    positionsCount: 5,
    workModel: "Hybrid (primärt distans, viss närvaro på plats)",
    optionDescription: "2 + 2 år, längst till 2030",
    contractDuration: "2026–2028",
    submissionDeadline: "2025-12-19 (frågestopp 2025-12-05)",
  },
}

// ---------------------------------------------------------------------------
// case-003: Run 1 — complete analysis (no-go)
// ---------------------------------------------------------------------------

const CASE_003_RUN_1: TenderRun = {
  id: "run-003-1",
  runNumber: 1,
  createdAt: "2025-09-03T08:15:00Z",
  procurementDocuments: [
    {
      id: "doc-020",
      filename: "FK_Forfragan_Systemutveckling_2025.pdf",
      uploadedAt: "2025-09-03T08:16:00Z",
      status: "indexerad",
      pages: 42,
      sizeKb: 1980,
    },
    {
      id: "doc-021",
      filename: "FK_Bilaga_1_Kravspecifikation.pdf",
      uploadedAt: "2025-09-03T08:17:00Z",
      status: "indexerad",
      pages: 28,
      sizeKb: 1340,
    },
    {
      id: "doc-022",
      filename: "FK_Bilaga_2_Sekretess_och_Sakerhet.pdf",
      uploadedAt: "2025-09-03T08:18:00Z",
      status: "indexerad",
      pages: 18,
      sizeKb: 890,
    },
  ],
  contextDocuments: [
    {
      id: "doc-023",
      filename: "FK_Konsultprofiler_Bemanning.pdf",
      uploadedAt: "2025-09-03T08:19:00Z",
      status: "indexerad",
      pages: 9,
      sizeKb: 390,
    },
  ],
  bidDocuments: [],
  cvDocuments: [],
  requirements: [
    {
      id: "FK-SKA-001",
      description:
        "Leverantören ska ha godkänd säkerhetsackreditering nivå GS/T-3 utfärdad av MSB eller motsvarande behörig myndighet.",
      source: {
        kind: "file",
        documentName: "FK_Bilaga_2_Sekretess_och_Sakerhet.pdf",
        page: 4,
      } satisfies SourceRef,
      confidence: "saker",
      category: "Säkerhetskrav",
      isMandatory: true,
      snippet: snippet(
        "doc-022",
        "FK_Bilaga_2_Sekretess_och_Sakerhet.pdf",
        4,
        "Leverantören ska inneha en godkänd säkerhetsackreditering av lägst nivå GS/T-3 utfärdad av MSB eller av annan behörig nationell myndighet.",
        "3.1 Säkerhetsackreditering\nAckrediteringen ska vara aktiv vid anbudstillfället och under hela avtalstiden."
      ),
    },
    {
      id: "FK-SKA-002",
      description: "All drift och behandling av personuppgifter ska lokaliseras inom Sverige.",
      source: {
        kind: "file",
        documentName: "FK_Bilaga_2_Sekretess_och_Sakerhet.pdf",
        page: 8,
      } satisfies SourceRef,
      confidence: "saker",
      category: "Säkerhetskrav",
      isMandatory: true,
      snippet: snippet(
        "doc-022",
        "FK_Bilaga_2_Sekretess_och_Sakerhet.pdf",
        8,
        "All drift, lagring och behandling av uppgifter inom systemet ska utföras på servrar fysiskt lokaliserade inom Sverige.",
        "3.4 Datalagring\nAlla undantag eller mellanhänder utanför Sverige är ej tillåtna."
      ),
    },
    {
      id: "FK-SKA-003",
      description:
        "Leverantören ska kunna leverera ett driftsklart system inom 4 månader från avtalsteckning.",
      source: {
        kind: "file",
        documentName: "FK_Forfragan_Systemutveckling_2025.pdf",
        page: 11,
      } satisfies SourceRef,
      confidence: "saker",
      category: "Tidskrav",
      isMandatory: true,
      snippet: snippet(
        "doc-020",
        "FK_Forfragan_Systemutveckling_2025.pdf",
        11,
        "Komplett och driftsklart system ska levereras och godkännas av beställaren senast fyra (4) månader efter avtalsteckning.",
        "4.2 Leveranstid\nFörseningsavgift utgår med 1 % av kontraktsvärdet per påbörjad förseningsvecka."
      ),
    },
    {
      id: "FK-SKA-004",
      description:
        "Leverantören ska ha bevisad erfarenhet av systemutveckling för offentliga socialförsäkringssystem.",
      source: {
        kind: "file",
        documentName: "FK_Bilaga_1_Kravspecifikation.pdf",
        page: 6,
      } satisfies SourceRef,
      confidence: "saker",
      category: "Erfarenhetskrav",
      isMandatory: true,
      snippet: snippet(
        "doc-021",
        "FK_Bilaga_1_Kravspecifikation.pdf",
        6,
        "Leverantören ska redovisa minst ett (1) slutfört uppdrag inom systemutveckling av handläggningssystem för socialförsäkring.",
        "2.2 Erfarenhetskrav\nUppdrag under de senaste fem (5) åren hos offentlig beställare."
      ),
    },
    {
      id: "FK-SKA-005",
      description:
        "Minst fem (5) konsulter i projektet ska ha säkerhetsgodkännande personskyddsklassning klass 3.",
      source: {
        kind: "file",
        documentName: "FK_Bilaga_2_Sekretess_och_Sakerhet.pdf",
        page: 12,
      } satisfies SourceRef,
      confidence: "saker",
      category: "Personalkrav",
      isMandatory: true,
      snippet: snippet(
        "doc-022",
        "FK_Bilaga_2_Sekretess_och_Sakerhet.pdf",
        12,
        "Minst fem (5) konsulter som arbetar med systemet ska ha aktiv personskyddsklassning klass 3 i enlighet med säkerhetsskyddslagen.",
        "4.1 Personalsäkerhet\nKlassningarna ska vara aktiva vid uppdragsstart."
      ),
    },
    {
      id: "FK-SKA-006",
      description:
        "Leverantören ska acceptera sekretessavtal med obegränsat skadeståndsansvar utan ansvarsbegränsning.",
      source: {
        kind: "file",
        documentName: "FK_Bilaga_2_Sekretess_och_Sakerhet.pdf",
        page: 15,
      } satisfies SourceRef,
      confidence: "saker",
      category: "Avtalsvillkor",
      isMandatory: true,
      snippet: snippet(
        "doc-022",
        "FK_Bilaga_2_Sekretess_och_Sakerhet.pdf",
        15,
        "Leverantören accepterar obegränsat skadeståndsansvar vid brott mot sekretessbestämmelserna i detta avtal.",
        "5.2 Skadeståndsansvar\nIngen ansvarsbegränsning gäller för sekretessöverträdelser."
      ),
    },
  ],
  evaluationCriteria: [
    {
      id: "FK-UTV-001",
      name: "Teknisk lösning",
      weightPercent: 50,
      description: "Kvalitet och lämplighet hos den föreslagna tekniska arkitekturen.",
      source: {
        kind: "file",
        documentName: "FK_Forfragan_Systemutveckling_2025.pdf",
        page: 18,
      } satisfies SourceRef,
      confidence: "saker",
      snippet: snippet(
        "doc-020",
        "FK_Forfragan_Systemutveckling_2025.pdf",
        18,
        "Den tekniska lösningen utvärderas med 50 % vikt utifrån arkitekturell kvalitet, skalbarhet och säkerhetslösning.",
        "6.1 Utvärderingskriterier\nTeknisk lösning 50 %, Pris 30 %, Projektorganisation 20 %."
      ),
    },
    {
      id: "FK-UTV-002",
      name: "Pris och kostnadseffektivitet",
      weightPercent: 30,
      description: "Fast pris för hela leveransen inklusive 12 månaders garantiperiod.",
      source: {
        kind: "file",
        documentName: "FK_Forfragan_Systemutveckling_2025.pdf",
        page: 19,
      } satisfies SourceRef,
      confidence: "saker",
      snippet: snippet(
        "doc-020",
        "FK_Forfragan_Systemutveckling_2025.pdf",
        19,
        "Anbudet ska innehålla ett fast pris för fullständig leverans inklusive tolv (12) månaders garantiperiod.",
        "6.2 Prisutvärdering\nFast pris, priset viktas med 30 %."
      ),
    },
    {
      id: "FK-UTV-003",
      name: "Projektorganisation och kompetens",
      weightPercent: 20,
      description: "Nyckelkonsulternas meriter och projektledningsmodell.",
      source: {
        kind: "file",
        documentName: "FK_Bilaga_1_Kravspecifikation.pdf",
        page: 14,
      } satisfies SourceRef,
      confidence: "saker",
      snippet: snippet(
        "doc-021",
        "FK_Bilaga_1_Kravspecifikation.pdf",
        14,
        "Projektorganisationen och nyckelkonsulternas meriter utvärderas med 20 % vikt.",
        "4.3 Projektorganisation (20 %)\nCV för projektledare, arkitekt och säkerhetsansvarig ska bifogas."
      ),
    },
  ],
  contractTerms: [
    {
      id: "FK-AVTAL-001",
      clause: "Obegränsat skadeståndsansvar vid sekretessbrott",
      description:
        "Leverantören accepterar obegränsat skadeståndsansvar utan tak vid sekretessöverträdelse.",
      riskLevel: "hog",
      flagged: true,
      source: {
        kind: "file",
        documentName: "FK_Bilaga_2_Sekretess_och_Sakerhet.pdf",
        page: 15,
      } satisfies SourceRef,
      snippet: snippet(
        "doc-022",
        "FK_Bilaga_2_Sekretess_och_Sakerhet.pdf",
        15,
        "Leverantören accepterar obegränsat skadeståndsansvar vid brott mot sekretessbestämmelserna. Ingen ansvarsbegränsning gäller.",
        "5.2 Skadeståndsansvar — obegränsat, ej förhandlingsbart."
      ),
    },
    {
      id: "FK-AVTAL-002",
      clause: "Förseningsavgift 1 % per vecka utan tak",
      description:
        "Vid försenad leverans utgår avgift om 1 % av kontraktsvärdet per påbörjad förseningsvecka utan övre tak.",
      riskLevel: "hog",
      flagged: true,
      source: {
        kind: "file",
        documentName: "FK_Forfragan_Systemutveckling_2025.pdf",
        page: 11,
      } satisfies SourceRef,
      snippet: snippet(
        "doc-020",
        "FK_Forfragan_Systemutveckling_2025.pdf",
        11,
        "Förseningsavgift utgår med 1 % av kontraktsvärdet per påbörjad förseningsvecka.",
        "4.2 Leveranstid — förseningsavgift utan tak."
      ),
    },
    {
      id: "FK-AVTAL-003",
      clause: "Full äganderätt till källkod och dokumentation",
      description: "Försäkringskassan erhåller full äganderätt till all källkod och leverabler.",
      riskLevel: "medel",
      flagged: false,
      source: {
        kind: "file",
        documentName: "FK_Bilaga_2_Sekretess_och_Sakerhet.pdf",
        page: 10,
      } satisfies SourceRef,
      snippet: snippet(
        "doc-022",
        "FK_Bilaga_2_Sekretess_och_Sakerhet.pdf",
        10,
        "Samtliga immateriella rättigheter till systemet, källkod och dokumentation övergår till Försäkringskassan vid leverans.",
        "4.5 Immateriella rättigheter."
      ),
    },
  ],
  criticalDates: [
    {
      id: "FK-DATUM-001",
      event: "Sista dag för frågor",
      date: "2025-09-12",
      daysRemaining: -84,
      urgency: "kritisk",
      snippet: snippet(
        "doc-020",
        "FK_Forfragan_Systemutveckling_2025.pdf",
        30,
        "Skriftliga frågor ska vara inkomna senast 2025-09-12 kl. 12:00.",
        "8.1 Frågestopp — passerat."
      ),
    },
    {
      id: "FK-DATUM-002",
      event: "Anbudstidens utgång",
      date: "2025-09-26",
      daysRemaining: -151,
      urgency: "kritisk",
      snippet: snippet(
        "doc-020",
        "FK_Forfragan_Systemutveckling_2025.pdf",
        31,
        "Anbud ska vara Försäkringskassan tillhanda senast 2025-09-26 klockan 14:00.",
        "8.3 Inlämning — passerat."
      ),
    },
    {
      id: "FK-DATUM-003",
      event: "Planerad avtalsteckning",
      date: "2025-10-15",
      daysRemaining: -162,
      urgency: "normal",
    },
    {
      id: "FK-DATUM-004",
      event: "Leveransdeadline (4 månader från avtal)",
      date: "2026-02-15",
      daysRemaining: -10,
      urgency: "kritisk",
      snippet: snippet(
        "doc-020",
        "FK_Forfragan_Systemutveckling_2025.pdf",
        11,
        "Komplett och driftsklart system ska levereras senast fyra (4) månader efter avtalsteckning.",
        "4.2 Leveranstid."
      ),
    },
  ],
  requirementMatrix: [
    {
      requirementId: "FK-SKA-001",
      requirement: "Säkerhetsackreditering GS/T-3",
      status: "ej_uppfyllt",
      gap: "Bolaget saknar GS/T-ackreditering. Ansökningsprocessen tar 12–18 månader — omöjligt att erhålla inom anbudstiden.",
      evidence: evidence(
        "Kontroll mot MSB:s ackrediteringsregister visar att bolaget inte innehar någon aktiv GS/T-ackreditering. Ansökan har inte initierats. Processen kräver 12–18 månaders handläggningstid.",
        [
          snippet(
            "doc-022",
            "FK_Bilaga_2_Sekretess_och_Sakerhet.pdf",
            4,
            "Leverantören ska inneha en godkänd säkerhetsackreditering av lägst nivå GS/T-3 utfärdad av MSB.",
            "3.1 Säkerhetsackreditering — absolut krav vid anbudstillfället."
          ),
        ],
        "saker",
        {
          status: "contradicted",
          checkedAt: "2025-09-14T09:00:00Z",
          checkedBy: "ai",
          notes:
            "Showstopper: bolaget saknar GS/T-ackreditering och kan inte erhålla den inom anbudstiden.",
          conflicts: [
            {
              withClaimId: "FK-RISK-001",
              description: "Saknad GS/T-ackreditering är en direkt diskvalificeringsgrund.",
              severity: "high",
            },
          ],
        }
      ),
    },
    {
      requirementId: "FK-SKA-002",
      requirement: "Driftsättning inom Sverige",
      status: "delvis",
      gap: "Primär infrastruktur finns utanför Sverige (Azure West Europe). Flytt till Azure Sweden Central möjlig men kräver 3–4 månader och merkostnad.",
      evidence: evidence(
        "Bolagets molninfrastruktur är primärt lokaliserad i Azure West Europe. Azure Sweden Central finns tillgänglig men migration kräver 3–4 månader och priset ökar med 15–20%.",
        [
          snippet(
            "doc-022",
            "FK_Bilaga_2_Sekretess_och_Sakerhet.pdf",
            8,
            "All drift, lagring och behandling av uppgifter inom systemet ska utföras på servrar fysiskt lokaliserade inom Sverige.",
            "3.4 Datalagring — servrar ska vara i Sverige."
          ),
        ],
        "osaker",
        { status: "partially_verified", checkedAt: "2025-09-14T09:30:00Z", checkedBy: "ai" }
      ),
    },
    {
      requirementId: "FK-SKA-003",
      requirement: "Leverans inom 4 månader",
      status: "ej_uppfyllt",
      gap: "Intern estimering: 9–10 månader för detta projektscope. 4-månaders kravet är tekniskt ogörligt.",
      evidence: evidence(
        "Projektscopet inkluderar 12 integrationsytor mot FK:s kärnsystem, nytt handläggningsgränssnitt och fullständig säkerhetsanpassning. Intern estimering: 9–11 månader. 4-månaders kravet är omöjligt.",
        [
          snippet(
            "doc-020",
            "FK_Forfragan_Systemutveckling_2025.pdf",
            11,
            "Komplett och driftsklart system ska levereras och godkännas av beställaren senast fyra (4) månader efter avtalsteckning.",
            "4.2 Leveranstid — 4 månader, inga undantag."
          ),
        ],
        "saker",
        {
          status: "contradicted",
          checkedAt: "2025-09-14T10:00:00Z",
          checkedBy: "ai",
          notes: "Showstopper: 4-månaders leveranskrav är inte genomförbart.",
          conflicts: [
            {
              withClaimId: "FK-RISK-002",
              description:
                "Leveransrisk kopplad till omöjlig tidslinje och 1%/vecka förseningsavgift.",
              severity: "high",
            },
          ],
        }
      ),
    },
    {
      requirementId: "FK-SKA-004",
      requirement: "Erfarenhet av socialförsäkringssystem",
      status: "ej_uppfyllt",
      gap: "Referensportföljen saknar uppdrag inom socialförsäkring. Befintliga offentliga uppdrag (Region Stockholm, Göteborg Stad) uppfyller inte kravet.",
      evidence: evidence(
        "Granskning av referensportföljen visar inga uppdrag inom socialförsäkring eller liknande välfärdssystem. Befintliga offentliga uppdrag uppfyller inte det specifika kravet.",
        [
          snippet(
            "doc-021",
            "FK_Bilaga_1_Kravspecifikation.pdf",
            6,
            "Leverantören ska redovisa minst ett (1) slutfört uppdrag inom systemutveckling av handläggningssystem för socialförsäkring.",
            "2.2 Erfarenhetskrav — socialförsäkring eller välfärdssystem krävs."
          ),
        ],
        "saker",
        { status: "contradicted", checkedAt: "2025-09-14T10:30:00Z", checkedBy: "ai" }
      ),
    },
    {
      requirementId: "FK-SKA-005",
      requirement: "5 konsulter med säkerhetsgodkännande klass 3",
      status: "ej_uppfyllt",
      gap: "Bolaget har 1 konsult med aktivt klass 3-godkännande. Kravet är 5. Processen tar 6–12 månader per konsult.",
      evidence: evidence(
        "Säkerhetsregistret visar 1 aktiv konsult med personskyddsklassning klass 3. Ytterligare 4 saknar klassning. Processen per individ tar 6–12 månader — kravet kan inte uppfyllas inom anbudstiden.",
        [
          snippet(
            "doc-022",
            "FK_Bilaga_2_Sekretess_och_Sakerhet.pdf",
            12,
            "Minst fem (5) konsulter ska ha aktiv personskyddsklassning klass 3.",
            "4.1 Personalsäkerhet — 5 konsulter krävs med klass 3."
          ),
        ],
        "saker",
        {
          status: "contradicted",
          checkedAt: "2025-09-14T11:00:00Z",
          checkedBy: "ai",
          notes: "Showstopper: 1 av 5 klassningar aktiva.",
        }
      ),
    },
    {
      requirementId: "FK-SKA-006",
      requirement: "Obegränsat skadeståndsansvar sekretess",
      status: "ej_uppfyllt",
      gap: "Juridisk avdelning har nekats att acceptera obegränsat skadeståndsansvar. Villkoret är icke-förhandlingsbart.",
      evidence: evidence(
        "Juridisk avdelning bekräftar att §5.2 inte kan accepteras. Bolaget exponeras för ansvar vida överstigande kontraktsvärdet. Styrelsebeslut kräver alltid ansvarsbegränsning i leverantörsavtal.",
        [
          snippet(
            "doc-022",
            "FK_Bilaga_2_Sekretess_och_Sakerhet.pdf",
            15,
            "Leverantören accepterar obegränsat skadeståndsansvar vid brott mot sekretessbestämmelserna. Ingen ansvarsbegränsning gäller.",
            "5.2 Skadeståndsansvar — obegränsat, ej förhandlingsbart."
          ),
        ],
        "saker",
        {
          status: "contradicted",
          checkedAt: "2025-09-14T11:30:00Z",
          checkedBy: "ai",
          notes: "Showstopper: juridisk avdelning nekar acceptans.",
        }
      ),
    },
  ],
  risks: [
    {
      id: "FK-RISK-001",
      title: "GS/T-ackreditering saknas helt",
      description:
        "Bolaget saknar GS/T-3-ackreditering. Ansökningsprocessen tar 12–18 månader. Direkt diskvalificeringsgrund.",
      severity: 5,
      probability: 5,
      isShowstopper: true,
      mitigationNote:
        "Ingen mitigation möjlig inom anbudstiden. Bolaget bör initiera ackrediteringsansökan för framtida upphandlingar av denna typ.",
      evidence: evidence(
        "GS/T-ackreditering är ett obligatoriskt grundkrav som inte kan kringgås. MSB:s handläggningstid är 12–18 månader. Diskvalificering är oundviklig.",
        [
          snippet(
            "doc-022",
            "FK_Bilaga_2_Sekretess_och_Sakerhet.pdf",
            4,
            "Leverantören ska inneha en godkänd säkerhetsackreditering av lägst nivå GS/T-3.",
            "3.1 Säkerhetsackreditering — absolut krav."
          ),
        ],
        "saker",
        { status: "verified", checkedAt: "2025-09-15T09:00:00Z", checkedBy: "ai" }
      ),
    },
    {
      id: "FK-RISK-002",
      title: "Leveranstid 4 månader är tekniskt ogörlig",
      description:
        "Projektomfattningen kräver 9–10 månader. Förseningsavgifterna på 1%/vecka utan tak gör det affärsmässigt oacceptabelt.",
      severity: 5,
      probability: 5,
      isShowstopper: true,
      mitigationNote:
        "Ingen mitigation möjlig — tidslinjekravet är formellt och förseningsavgifterna saknar tak.",
      evidence: evidence(
        "Intern estimering ger 9–11 månader. Förseningsavgiften på 1%/vecka utan tak exponerar bolaget för ansvar på 25–40% av kontraktsvärdet.",
        [
          snippet(
            "doc-020",
            "FK_Forfragan_Systemutveckling_2025.pdf",
            11,
            "Komplett och driftsklart system ska levereras senast fyra (4) månader efter avtalsteckning. Förseningsavgift 1 % per vecka.",
            "4.2 Leveranstid med förseningsavgift utan tak."
          ),
        ],
        "saker",
        { status: "verified", checkedAt: "2025-09-15T09:30:00Z", checkedBy: "ai" }
      ),
    },
    {
      id: "FK-RISK-003",
      title: "Obegränsat skadeståndsansvar — juridisk avdelning nekar",
      description:
        "Sekretessavtalets §5.2 ger obegränsat ansvar. Juridisk avdelning har formellt nekat acceptans.",
      severity: 5,
      probability: 4,
      isShowstopper: true,
      mitigationNote: "Beställaren tillåter inga avvikelser. Risken kan inte mitigeras.",
      evidence: evidence(
        "Juridisk avdelning har formellt avrått från att acceptera §5.2. Bolaget exponeras för potentiell skada vida överstigande kontraktsvärdet.",
        [
          snippet(
            "doc-022",
            "FK_Bilaga_2_Sekretess_och_Sakerhet.pdf",
            15,
            "Leverantören accepterar obegränsat skadeståndsansvar. Ingen ansvarsbegränsning gäller.",
            "5.2 Obegränsat ansvar — juridisk avdelning nekar acceptans."
          ),
        ],
        "saker",
        { status: "verified", checkedAt: "2025-09-15T10:00:00Z", checkedBy: "ai" }
      ),
    },
    {
      id: "FK-RISK-004",
      title: "Referenskrav socialförsäkring uppfylls inte",
      description:
        "Erfarenhetskravet på socialförsäkringssystem kan inte styrkas med befintliga uppdrag.",
      severity: 4,
      probability: 5,
      isShowstopper: false,
      mitigationNote:
        "Möjligt att begära förtydligande om angränsande välfärdssystem accepteras — underordnat övriga showstoppers.",
      evidence: evidence(
        "Referensportföljen täcker kommunal IT och regionupphandlingar men saknar socialförsäkring.",
        [
          snippet(
            "doc-021",
            "FK_Bilaga_1_Kravspecifikation.pdf",
            6,
            "Leverantören ska redovisa minst ett (1) slutfört uppdrag inom systemutveckling av handläggningssystem för socialförsäkring.",
            "2.2 Erfarenhetskrav."
          ),
        ],
        "saker",
        { status: "verified", checkedAt: "2025-09-15T10:30:00Z", checkedBy: "ai" }
      ),
    },
  ],
  scorecardDimensions: [
    {
      id: "d1",
      label: "Kravuppfyllnad",
      score: 18,
      description: "4 av 6 ska-krav ej uppfyllda, varav 3 är absoluta diskvalificeringsgrunder",
      evidence: evidence(
        "Av 6 ska-krav är 4 ej uppfyllda (FK-SKA-001, 003, 005, 006) och 1 delvis uppfyllt (FK-SKA-002). De ej uppfyllda kraven inkluderar GS/T-ackreditering, leveranstid och säkerhetsklassad personal.",
        [],
        "saker",
        { status: "verified", checkedAt: "2025-09-15T11:00:00Z", checkedBy: "ai" }
      ),
    },
    {
      id: "d2",
      label: "Riskprofil",
      score: 8,
      description: "3 showstoppers utan mitigation — anbud kan inte lämnas",
      evidence: evidence(
        "Riskmatrisen innehåller 3 showstoppers som samtliga är utan tillgänglig mitigation.",
        [],
        "saker",
        { status: "verified", checkedAt: "2025-09-15T11:01:00Z", checkedBy: "ai" }
      ),
    },
    {
      id: "d3",
      label: "Tidshållbarhet",
      score: 5,
      description: "4-månaders leveranskrav är tekniskt ogörligt — intern estimering: 9–10 månader",
      evidence: evidence(
        "Leveranskravet på 4 månader är mer än dubbelt för kort mot intern estimering. Förseningsavgiften på 1%/vecka utan tak gör det affärsmässigt oacceptabelt.",
        [],
        "saker",
        { status: "verified", checkedAt: "2025-09-15T11:02:00Z", checkedBy: "ai" }
      ),
    },
    {
      id: "d4",
      label: "Strategisk passning",
      score: 32,
      description: "Tekniskt intressant uppdrag men kompetens- och certifieringsgap är för stora",
      evidence: evidence(
        "Upphandlingens tekniska scope matchar bolagets systemutvecklingskompetens men irrelevant givet att grundförutsättningarna saknas.",
        [],
        "osaker",
        { status: "partially_verified", checkedAt: "2025-09-15T11:03:00Z", checkedBy: "ai" }
      ),
    },
  ],
  recommendation: {
    verdict: "no_go",
    confidence: 97,
    summary:
      "Tre absoluta showstoppers omöjliggör deltagande: (1) bolaget saknar GS/T-3-ackreditering och kan inte erhålla den inom anbudstiden, (2) leveranskravet på 4 månader är tekniskt ogörligt med intern estimering på 9–10 månader och förseningsavgifter utan tak, (3) juridisk avdelning nekar acceptans av obegränsat skadeståndsansvar i sekretessavtalet. Rekommendationen är ett entydigt NO-GO.",
    reasonsFor: [],
    reasonsAgainst: [
      {
        text: "GS/T-3-ackreditering saknas — direkt diskvalificeringsgrund",
        evidence: evidence(
          "Bolaget saknar aktiv GS/T-ackreditering och ansökningsprocessen tar 12–18 månader.",
          [
            snippet(
              "doc-022",
              "FK_Bilaga_2_Sekretess_och_Sakerhet.pdf",
              4,
              "Leverantören ska inneha en godkänd säkerhetsackreditering av lägst nivå GS/T-3.",
              "3.1 Säkerhetsackreditering."
            ),
          ],
          "saker",
          { status: "verified", checkedAt: "2025-09-15T10:00:00Z", checkedBy: "ai" }
        ),
      },
      {
        text: "Leveranstid 4 månader är tekniskt ogörlig — intern estimering 9–10 månader",
        evidence: evidence(
          "Projektomfattningen kräver 9–11 månader. Förseningsavgiften på 1%/vecka saknar tak.",
          [
            snippet(
              "doc-020",
              "FK_Forfragan_Systemutveckling_2025.pdf",
              11,
              "Komplett och driftsklart system ska levereras senast fyra (4) månader. Förseningsavgift 1 % per vecka.",
              "4.2 Leveranstid."
            ),
          ],
          "saker",
          { status: "verified", checkedAt: "2025-09-15T10:01:00Z", checkedBy: "ai" }
        ),
      },
      {
        text: "Obegränsat skadeståndsansvar — juridisk avdelning nekar",
        evidence: evidence(
          "Sekretessavtalets §5.2 ger obegränsat ansvar. Beställaren tillåter inga avvikelser.",
          [
            snippet(
              "doc-022",
              "FK_Bilaga_2_Sekretess_och_Sakerhet.pdf",
              15,
              "Leverantören accepterar obegränsat skadeståndsansvar. Ingen ansvarsbegränsning gäller.",
              "5.2 Obegränsat skadeståndsansvar."
            ),
          ],
          "saker",
          { status: "verified", checkedAt: "2025-09-15T10:02:00Z", checkedBy: "ai" }
        ),
      },
      {
        text: "Referenskrav på socialförsäkringssystem kan inte styrkas",
        evidence: evidence(
          "Referensportföljen saknar uppdrag inom socialförsäkring. Befintliga offentliga uppdrag uppfyller inte kravet.",
          [
            snippet(
              "doc-021",
              "FK_Bilaga_1_Kravspecifikation.pdf",
              6,
              "Leverantören ska redovisa minst ett (1) slutfört uppdrag inom systemutveckling av handläggningssystem för socialförsäkring.",
              "2.2 Erfarenhetskrav."
            ),
          ],
          "saker",
          { status: "verified", checkedAt: "2025-09-15T10:03:00Z", checkedBy: "ai" }
        ),
      },
    ],
    uncertainties: [],
    actionItems: [
      {
        id: "FK-ACT-001",
        text: "Initiera GS/T-3-ackrediteringsansökan hos MSB omedelbart för att öppna möjligheten att delta i framtida liknande upphandlingar (ansökningstid 12–18 månader).",
        priority: "medel",
        relatedIds: ["FK-SKA-001", "FK-RISK-001"],
      },
    ],
  },
  procurementOverview: {
    description:
      "Försäkringskassan söker en leverantör för att utveckla och driftsätta ett nytt handläggningssystem för socialförsäkringsärenden. Systemet ska hantera ärendeflöden för sjukpenning och aktivitetsersättning. Uppdraget kräver GS/T-3-säkerhetsackreditering, leverans inom 4 månader och innebär obegränsat skadeståndsansvar vid avtalsbrott.",
    highlights: [
      "Systemutveckling av handläggningssystem för socialförsäkring",
      "Krav på GS/T-3-säkerhetsackreditering (ansökningstid 12–18 månader)",
      "Leveranstid 4 månader — förseningsavgift 1 %/vecka utan tak",
      "Obegränsat skadeståndsansvar och inga avtalsavvikelser tillåtna",
    ],
    procurementType: "Enskilt uppdrag",
    domain: "Systemutveckling",
    client: "Försäkringskassan",
    contractStandard: "Beställarens egna villkor",
    estimatedValue: "Ej specificerat",
    scopeSummary: "Fullständig systemleverans inkl. driftsättning",
    positionsCount: 5,
    workModel: "Primärt på plats (säkerhetsklassat uppdrag)",
  },
  roleRequirements: [
    {
      id: "FK-ROLL-001",
      roleName: "Systemutvecklare (säkerhetsklassad)",
      quantity: 5,
      educationLevel: "Högskolenivå",
      minYearsExperience: 4,
      requiredCertifications: ["Personskyddsklassning klass 3"],
      requiredExperience: [
        "Systemutveckling av handläggningssystem",
        "Erfarenhet av socialförsäkringssystem eller liknande välfärdssystem",
      ],
      meritingQualifications: [],
      source: {
        kind: "file",
        documentName: "FK_Bilaga_2_Sekretess_och_Sakerhet.pdf",
        page: 12,
      } satisfies SourceRef,
    },
  ],
  bidDeliverables: [],
}

// ---------------------------------------------------------------------------
// MOCK_TENDER_CASES
// ---------------------------------------------------------------------------

export const MOCK_TENDER_CASES: TenderCase[] = [
  {
    id: "case-001",
    title: "Ramavtal IT-konsulttjänster Region Stockholm 2026",
    isTitleAutoGenerated: false,
    reference: "RS-UPP-2025-0847",
    createdAt: "2025-11-18T09:30:00Z",
    updatedAt: "2025-11-20T14:15:00Z",
    submissionDeadline: "2026-03-10",
    status: "analyserad",
    runs: [CASE_001_RUN_1, CASE_001_RUN_2],
    activeRunId: "run-001-2",
  },
  {
    id: "case-002",
    title: "Digital infrastruktur Malmö Stad 2026",
    isTitleAutoGenerated: false,
    reference: "MMS-UPP-2025-0312",
    createdAt: "2025-11-10T11:00:00Z",
    updatedAt: "2025-11-10T11:00:00Z",
    submissionDeadline: "2026-03-03",
    status: "utkast",
    runs: [
      {
        id: "run-002-1",
        runNumber: 1,
        createdAt: "2025-11-10T11:00:00Z",
        procurementDocuments: [
          {
            id: "doc-010",
            filename: "FU_Digital_Infrastruktur_Malmo_2026.pdf",
            uploadedAt: "2025-11-10T11:01:00Z",
            status: "indexeras",
            pages: 35,
            sizeKb: 1820,
          },
        ],
        contextDocuments: [],
        bidDocuments: [],
        cvDocuments: [],
        requirements: [],
        evaluationCriteria: [],
        contractTerms: [],
        criticalDates: [],
        requirementMatrix: [],
        risks: [],
        scorecardDimensions: [],
        recommendation: null,
        roleRequirements: [],
        bidDeliverables: [],
      },
    ],
    activeRunId: "run-002-1",
  },
  {
    id: "case-003",
    title: "Systemutveckling Försäkringskassan 2025",
    isTitleAutoGenerated: false,
    reference: "FK-UPP-2025-0044",
    createdAt: "2025-09-03T08:15:00Z",
    updatedAt: "2025-09-15T16:45:00Z",
    submissionDeadline: "2025-09-25",
    status: "no_go",
    runs: [CASE_003_RUN_1],
    activeRunId: "run-003-1",
  },
]

export const getMockCase = (id: string): TenderCase | undefined =>
  MOCK_TENDER_CASES.find((c) => c.id === id)

export const MOCK_REQUIREMENT_MATRIX = CASE_001_RUN_2.requirementMatrix
export const MOCK_RISKS = CASE_001_RUN_2.risks
export const MOCK_SCORECARD_DIMENSIONS = CASE_001_RUN_2.scorecardDimensions
export const MOCK_RECOMMENDATION = CASE_001_RUN_2.recommendation!
export const MOCK_BID_DELIVERABLES = CASE_001_RUN_2.bidDeliverables
export const MOCK_PROCUREMENT_OVERVIEW = CASE_001_RUN_2.procurementOverview
